import { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import EtapasPostulacionChip from "./EtapasPostulacionChip";
import {
  AB_VARIANT_FILTER_OPTIONS,
  EtapaFormularioCell,
  FORM_STAGE_FILTER_OPTIONS,
  IExperimentoPostulacion,
  STAGE_WHATSAPP_FILTER_OPTIONS,
  VarianteABCell,
  WhatsappEtapaCell,
} from "./ExperimentoPostulacionCells";
import { enviarInvitacionAhora, extenderInvitacion } from "../../../services/invitacionEtapaService";
import { CompletitudPostulacionCell, ICompletitudPostulacion } from "./CompletitudPostulacionCell";
import ConfiguracionCompletitudModal from "./ConfiguracionCompletitudModal";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory, useLocation } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Tooltip from "../../ui/kit-mdtw/tooltip/Tooltip";
import { IDetalleCedulaItem } from "../../../interfaces/ISeleccion";
import PillValidacionCedula, { cedulaBadgeTooltip, resolveCedulaBadge } from "../../ui/groups/PillValidacionCedula";
import { OPCIONES_FILTRO_CEDULA } from "../../../utils/filtroCedula";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SyncIcon from "@mui/icons-material/Sync";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import TablaFormularios from "./TablaFormularios";
import { IPrefilterSelection } from "./prefiltrado/prefiltradoTypes";
import ColumnVisibilityMenu from "../../ui/ColumnVisibilityMenu";
import { columnOrderStorageKey, mergeColumnOrder, readColumnOrder, renderOrderedCells } from "../../ui/columnOrder";
import TableSearchInput from "../../ui/TableSearchInput";
import ModalAsignarPostulantes from "./ModalAsignarPostulantes";
import SearchSelect, { SearchSelectOption } from "../../ui/groups/SearchSelect";
import DocumentoConVisor from "../../ui/kit-mdtw/documentViewer/DocumentoConVisor";
import { normalizarGiro, rotarDocumento } from "../../ui/kit-mdtw/documentViewer/rotarDocumento";
import "../../../assets/seleccionMasiva/carga-nomina-inhabilitados.scss";
import { syncRegistros } from "../../../services/syncRegistrosService";
import {
  getEstadoMigracionDatos,
  IMigracionDatosCorrida,
  migrarDatosPostulantes,
} from "../../../services/migrarDatosPostulantesService";
import {
  calcularCompletitud,
  getEstadoCalculoCompletitud,
  ICalculoCompletitudCorrida,
} from "../../../services/completitudPostulacionesService";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { aplicarSugerenciasIA, comentarioSugerenciasIA, etiquetaCampoCedula, ISugerenciaAplicada, ISugerenciaOmitida } from "./sugerenciasIA";
import { FILTER_PROFILE } from "../../../diccionary/Diccionary";
import { isTwMarketingProfile } from "../../../config/twMarketingAccess";
import {
  AVISO_DESCARGA_SIN_SELECCION,
  excedeLimiteDescarga,
  leerRecorteDescarga,
  MAX_REGISTROS_DESCARGA,
  mensajeDescargaRecortada,
  mensajeLimiteDescarga,
} from "../../../utils/limiteDescarga";
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
  getUserErrorMessage,
  parsePageSize,
  parseRutList,
  OBSERVACION_SIN_CAMBIOS,
  readApiErrorMessage,
  resolveDisplayedProcessContext,
  sincronizarObservacion,
} from "./postulacionesUtils";

// ─── types ────────────────────────────────────────────────────────────────────

type FilterKey =
  | "code"
  | "formCode"
  | "formCategory"
  | "DNI"
  | "names"
  | "client"
  | "business"
  | "vacancy"
  | "recruiter"
  | "createdAt"
  | "status"
  | "shift"
  | "position"
  | "locality"
  | "gender"
  | "phone"
  | "cedula"
  | "abVariant"
  | "formStage"
  | "stageWhatsapp";

type ComputedStatus =
  | "PROCESADO"
  | "INHABILITADO"
  | "INHABILITADO_CLIENTE"
  | "INHABILITADO_PROCESO"
  | "VACANTE_INACTIVA"
  | "ASIGNADO"
  | "PROCESO_ACTIVO"
  | "DISPONIBLE";

interface IApplicationApi {
  applicationId: string;
  code: string;
  formCode?: string;
  formType: string;
  formCategory?: string;
  personId: string;
  DNI: string;
  names: string;
  clientId?: string;
  client: string;
  businessArea: string;
  businessAreaCode?: string;
  recruiter: string;
  recruiterId?: string;
  vacancy: string;
  createdAt: string;
  computedStatus: ComputedStatus;
  blockReason?: string;
  blockLevel?: string;
  blockDate?: string;
  blockComment?: string;
  blockRegisteredBy?: string;
  blockProcessId?: string;
  activeProcessId?: string;
  activeProcessBusinessAreaCode?: string;
  activeProcessDate?: string;
  activeProcessRecruiter?: string;
  shift?: string;
  position?: string;
  locality?: string;
  gender?: string;
  phone?: string;
  /**
   * Estado de la validación de cédula. `null`/`undefined` cuando nunca se
   * validó, que es lo habitual en los registros anteriores al flujo.
   */
  veredictoCedula?: string | null;
  /** Qué campo(s) discrepan. Solo viene con "requiere_revision". */
  detalleCedula?: IDetalleCedulaItem[] | null;
  // Postulación por etapas (null en postulaciones anteriores).
  stageMode?: string | null;
  stageStatus?: string | null;
  completedStages?: number | null;
  totalStages?: number | null;
  // A/B y WhatsApp de la etapa siguiente: ver IExperimentoPostulacion.
  experimentCode?: IExperimentoPostulacion["experimentCode"];
  variantCode?: IExperimentoPostulacion["variantCode"];
  variantName?: IExperimentoPostulacion["variantName"];
  isControlVariant?: IExperimentoPostulacion["isControlVariant"];
  forcedVariant?: IExperimentoPostulacion["forcedVariant"];
  invitationStatus?: IExperimentoPostulacion["invitationStatus"];
  invitationExpiresAt?: IExperimentoPostulacion["invitationExpiresAt"];
  invitationOpenedAt?: IExperimentoPostulacion["invitationOpenedAt"];
  invitationMessageStatus?: IExperimentoPostulacion["invitationMessageStatus"];
  invitationScheduledAt?: IExperimentoPostulacion["invitationScheduledAt"];
  invitationSentAt?: IExperimentoPostulacion["invitationSentAt"];
  invitationError?: IExperimentoPostulacion["invitationError"];
  /** Completitud de los datos (null si no se pudo calcular). */
  completeness?: ICompletitudPostulacion | null;
  totalRecords?: number;
}

interface IPaginationApi {
  totalRecords: number;
  currentRecords: number;
  currentLimit: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
  startIndex: number;
  endIndex: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
}

interface IApplicationListResponse {
  pagination: IPaginationApi;
  applications: IApplicationApi[];
}

type ActiveFilters = Record<FilterKey, string[]>;
type FilterInputs  = Record<FilterKey, string>;

// ─── config ───────────────────────────────────────────────────────────────────

const FILTER_KEYS: FilterKey[] = [
  "code", "formCode", "formCategory", "DNI", "names", "client", "business", "vacancy", "recruiter", "createdAt", "status",
  "shift", "position", "locality", "gender", "phone", "cedula",
  "abVariant", "formStage", "stageWhatsapp",
];

interface FilterColumn {
  key: FilterKey;
  label: string;
  placeholder: string;
  isSelect?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

type TableColumnKey =
  | "code"
  | "aiValidation"
  | "completeness"
  | "DNI"
  | "names"
  | "formCode"
  | "formType"
  | "formCategory"
  | "client"
  | "shift"
  | "position"
  | "locality"
  | "gender"
  | "phone"
  | "businessArea"
  | "vacancy"
  | "recruiter"
  | "createdAt"
  | "abVariant"
  | "formStage"
  | "stageWhatsapp"
  | "status";

interface TableColumnDefinition {
  key: TableColumnKey;
  label: string;
  width: number;
}

const TABLE_COLUMN_DEFINITIONS: TableColumnDefinition[] = [
  { key: "code",         label: "Código",            width: 90 },
  { key: "aiValidation", label: "Validación",        width: 130 },
  { key: "completeness", label: "Completitud",       width: 130 },
  { key: "DNI",          label: "RUT",               width: 98 },
  { key: "names",        label: "Nombre",            width: 140 },
  { key: "gender",       label: "Género",            width: 100 },
  { key: "phone",        label: "Teléfono",          width: 115 },
  { key: "formCode",     label: "Formulario",        width: 88 },
  { key: "formType",     label: "Tipo",              width: 85 },
  { key: "formCategory", label: "Categoría",         width: 130 },
  { key: "client",       label: "Cliente",           width: 125 },
  { key: "shift",        label: "Turno",             width: 110 },
  { key: "position",     label: "Cargo",             width: 140 },
  { key: "locality",     label: "Comuna",            width: 120 },
  { key: "businessArea", label: "Área",              width: 74 },
  { key: "vacancy",      label: "Proceso",           width: 88 },
  { key: "recruiter",    label: "Reclutador",        width: 140 },
  { key: "createdAt",    label: "Fecha Postulación", width: 118 },
  { key: "abVariant",    label: "Versión A/B",       width: 140 },
  { key: "formStage",    label: "Etapa",             width: 112 },
  { key: "stageWhatsapp", label: "WhatsApp 2ª parte", width: 160 },
  { key: "status",       label: "Estado",            width: 150 },
];

// Orden por defecto de las columnas; el usuario lo cambia arrastrándolas en "Columnas".
const DEFAULT_COLUMN_ORDER: TableColumnKey[] = TABLE_COLUMN_DEFINITIONS.map(({ key }) => key);

const APPLICATIONS_TABLE_PREFERENCE_KEY = "POSTULACIONES";
// TW Marketing antes veía un subconjunto de columnas, y su preferencia guardada
// enumera solo esas. Ahora tiene acceso completo: se le revelan todas una única
// vez (después puede ocultar las que quiera desde "Columnas").
const TW_MARKETING_FULL_ACCESS_MIGRATION_VERSION = "v1";
const TABLE_SKELETON_ROW_LIMIT = 12;
const TABLE_VIRTUALIZATION_THRESHOLD = 100;
const TABLE_VIRTUAL_ROW_HEIGHT = 55;
const TABLE_VIRTUAL_OVERSCAN = 8;
const TABLE_VIRTUAL_VIEWPORT_HEIGHT = 620;

const normalizeVisibleColumns = (
  value: unknown,
  columnDefinitions: TableColumnDefinition[] = TABLE_COLUMN_DEFINITIONS,
): TableColumnKey[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const keys = columnDefinitions
    .map(({ key }) => key)
    .filter((key) => value.includes(key));
  return keys.length === value.length ? keys : null;
};

const readVisibleColumns = (
  storageKey: string,
  columnDefinitions: TableColumnDefinition[] = TABLE_COLUMN_DEFINITIONS,
): TableColumnKey[] => {
  try {
    return normalizeVisibleColumns(
      JSON.parse(localStorage.getItem(storageKey) ?? "[]"),
      columnDefinitions,
    ) ?? columnDefinitions.map(({ key }) => key);
  } catch {
    return columnDefinitions.map(({ key }) => key);
  }
};

// Las columnas nuevas no aparecen solas para quien ya tenía una preferencia
// guardada, porque la preferencia enumera exactamente las columnas visibles. Se
// fuerzan una única vez, igual que se hizo con "Área de negocio". Al agregar una
// columna se sube la versión para revelarla; se listan SOLO las nuevas, para no
// re-mostrar las que el usuario ocultó a propósito ("gender"/"phone" ya se
// revelaron en v1).
// v3: columnas del A/B ("Versión A/B", "Etapa", "WhatsApp 2ª parte").
// v4: "Completitud".
const PERSONAL_COLUMNS_MIGRATION_VERSION = "v4";
const PERSONAL_COLUMNS_TO_REVEAL: TableColumnKey[] = ["completeness"];

const withColumnsVisible = (
  keys: TableColumnKey[],
  columnDefinitions: TableColumnDefinition[],
  extraKeys: TableColumnKey[],
): TableColumnKey[] => {
  const visibleKeys = new Set<TableColumnKey>([...keys, ...extraKeys]);
  return columnDefinitions
    .map(({ key }) => key)
    .filter((key) => visibleKeys.has(key));
};

const needsBusinessAreaPreferenceMigration = (migrationKey: string): boolean => {
  try {
    return localStorage.getItem(migrationKey) !== "complete";
  } catch {
    return true;
  }
};

const PRIMARY_FILTER_COLUMNS: FilterColumn[] = [
  { key: "status",    label: "Estado",            placeholder: "Selecciona…",                isSelect: true },
  { key: "code",      label: "Código",            placeholder: "Escribe y presiona Enter" },
  { key: "formCode",  label: "Formulario",        placeholder: "Escribe y presiona Enter" },
  { key: "formCategory", label: "Categoría de formulario", placeholder: "Selecciona…",     isSelect: true },
  { key: "DNI",       label: "RUT",               placeholder: "Escribe y presiona Enter" },
  { key: "names",     label: "Nombre",            placeholder: "Escribe y presiona Enter" },
  { key: "gender",    label: "Género",            placeholder: "Selecciona un género",       isSelect: true },
  { key: "phone",     label: "Teléfono",          placeholder: "Escribe y presiona Enter" },
  { key: "client",    label: "Cliente",           placeholder: "Escribe y presiona Enter" },
  { key: "shift",     label: "Turno",             placeholder: "Selecciona un turno",        isSelect: true },
  { key: "position",  label: "Cargo",             placeholder: "Selecciona un cargo",        isSelect: true },
  { key: "locality",  label: "Comuna",            placeholder: "Selecciona una comuna",      isSelect: true },
  { key: "recruiter", label: "Reclutador",        placeholder: "Escribe y presiona Enter" },
  { key: "createdAt", label: "Fecha Postulación", placeholder: "DD-MM-AAAA" },
  { key: "cedula",    label: "Validación",        placeholder: "Selecciona…",                isSelect: true },
  { key: "abVariant", label: "Versión A/B",       placeholder: "Selecciona…",                isSelect: true },
  { key: "formStage", label: "Etapa",             placeholder: "Selecciona…",                isSelect: true },
  { key: "stageWhatsapp", label: "WhatsApp 2ª parte", placeholder: "Selecciona…",            isSelect: true },
];

const ADVANCED_FILTER_COLUMNS: FilterColumn[] = [
  { key: "vacancy",  label: "Proceso",         placeholder: "Escribe y presiona Enter" },
  { key: "business", label: "Área de negocio", placeholder: "Escribe y presiona Enter" },
];

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string; dot: string }> = {
  DISPONIBLE:           { label: "Disponible",              bg: "#d1fae5", color: "#047857", dot: "#10b981" },
  PROCESADO:            { label: "Procesado",               bg: "#ede9fe", color: "#6d28d9", dot: "#8b5cf6" },
  ASIGNADO:             { label: "Asignado",                bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
  PROCESO_ACTIVO:       { label: "En proceso activo",       bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" },
  VACANTE_INACTIVA:     { label: "Vacante inactiva",        bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
  INHABILITADO_PROCESO: { label: "Inhabilitado por proceso", bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
  INHABILITADO_CLIENTE: { label: "Inhabilitado por cliente", bg: "#fee2e2", color: "#b91c1c", dot: "#f87171" },
  INHABILITADO:         { label: "Inhabilitado",            bg: "#fecaca", color: "#991b1b", dot: "#ef4444" },
};

const ESTADO_FALLBACK = { label: "—", bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" };

const resolveEstado = (status: string | null | undefined) => {
  if (!status) return ESTADO_FALLBACK;
  const key = String(status).trim().toUpperCase().replace(/\s+/g, "_");
  if (ESTADO_CONFIG[key]) return ESTADO_CONFIG[key];
  return { ...ESTADO_FALLBACK, label: String(status) };
};

const formatBlockDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return dateFormatter.format(d);
};

const buildStatusMessage = (
  item: Pick<
    IApplicationApi,
    | "computedStatus"
    | "blockReason"
    | "blockDate"
    | "blockComment"
    | "blockRegisteredBy"
    | "blockProcessId"
    | "client"
    | "vacancy"
    | "activeProcessId"
    | "activeProcessBusinessAreaCode"
    | "activeProcessDate"
    | "activeProcessRecruiter"
  >,
): string => {
  const fecha = formatBlockDate(item.blockDate);
  const fechaProc = formatBlockDate(item.activeProcessDate);
  const por = item.blockRegisteredBy ? `, registrado por ${item.blockRegisteredBy}` : "";
  const cuando = fecha ? `, con fecha ${fecha}` : "";
  const comentario = item.blockComment ? ` — ${item.blockComment}` : "";

  switch (item.computedStatus) {
    case "INHABILITADO":
      return `Persona inhabilitada por ${item.blockReason ?? "causa no especificada"}${cuando}${por}${comentario}`;
    case "INHABILITADO_CLIENTE":
      return `Persona inhabilitada por cliente ${item.client || "—"}${cuando}${por}${comentario}`;
    case "INHABILITADO_PROCESO":
      return `Persona inhabilitada para proceso ${item.blockProcessId || "—"}${cuando}${por}${comentario}`;
    case "VACANTE_INACTIVA":
      return `La vacante ${item.vacancy || ""} ya no está activa`.trim();
    case "ASIGNADO":
      return `Postulante asignado a este proceso${fechaProc ? ` el ${fechaProc}` : ""}`;
    case "PROCESO_ACTIVO": {
      const partes: string[] = ["Persona activa en un proceso"];
      if (fechaProc) partes.push(`reclutado el ${fechaProc}`);
      if (item.activeProcessRecruiter) partes.push(`por ${item.activeProcessRecruiter}`);
      if (item.activeProcessId) partes.push(`en el proceso ${item.activeProcessId}`);
      if (item.activeProcessBusinessAreaCode) partes.push(`del área ${item.activeProcessBusinessAreaCode}`);
      return partes.join(", ");
    }
    case "PROCESADO":
      return "Postulación ya procesada";
    case "DISPONIBLE":
      return "Candidato disponible";
    default:
      return item.computedStatus || "—";
  }
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "DISPONIBLE",           label: "Disponible"               },
  { value: "PROCESADO",            label: "Procesado"                },
  { value: "ASIGNADO",             label: "Asignado"                 },
  { value: "PROCESO_ACTIVO",       label: "En proceso activo"        },
  { value: "VACANTE_INACTIVA",     label: "Vacante inactiva"         },
  { value: "INHABILITADO_PROCESO", label: "Inhabilitado por proceso" },
  { value: "INHABILITADO_CLIENTE", label: "Inhabilitado por cliente" },
  { value: "INHABILITADO",         label: "Inhabilitado"             },
];

/**
 * Estado de la validación de cédula.
 *
 * Los valores son los veredictos que produce el motor, salvo "SIN_VALIDAR",
 * que representa el `null` de la ficha — la mayoría de los registros
 * históricos, que nunca pasaron por la validación.
 *
 * "validado" y "completado" van juntos bajo una sola opción: para quien revisa
 * son lo mismo (los datos calzan con la cédula), y separarlos obligaría a
 * explicar una distinción del motor que no cambia ninguna decisión. "APROBADO"
 * también cae en "Datos validados": el backend lo agrega a esa opción.
 *
 * "Validado manualmente" (validado_manual) va aparte a propósito: es un
 * desenlace resuelto por una persona, no por el motor, y quien filtra suele
 * querer distinguir justamente eso.
 */
const CEDULA_OPTIONS: { value: string; label: string }[] = OPCIONES_FILTRO_CEDULA.map(
  ({ valor, etiqueta }) => ({ value: valor, label: etiqueta }),
);

const STATIC_SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  status: STATUS_OPTIONS,
  cedula: CEDULA_OPTIONS,
  abVariant: AB_VARIANT_FILTER_OPTIONS,
  formStage: FORM_STAGE_FILTER_OPTIONS,
  stageWhatsapp: STAGE_WHATSAPP_FILTER_OPTIONS,
};

interface MultiSelectFilterProps {
  options: FilterOption[];
  selectedValues: string[];
  placeholder: string;
  onToggle: (value: string) => void;
}

const MultiSelectFilter = ({
  options,
  selectedValues,
  placeholder,
  onToggle,
}: MultiSelectFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const visibleOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const summary = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
      ? options.find((option) => option.value === selectedValues[0])?.label ?? selectedValues[0]
      : `${selectedValues.length} seleccionados`;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="post-filter-input"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          minHeight: "34px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          cursor: "pointer",
          textAlign: "left",
          color: selectedValues.length > 0 ? "#334155" : "#94a3b8",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        <KeyboardArrowDownIcon
          style={{
            flexShrink: 0,
            fontSize: 18,
            color: "#64748b",
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: "absolute",
            top: "calc(100% + 5px)",
            left: 0,
            right: 0,
            zIndex: 30,
            padding: "7px",
            borderRadius: "9px",
            border: "1px solid #dbe3ee",
            background: "#fff",
            boxShadow: "0 10px 28px rgba(15,23,42,0.16)",
          }}
        >
          <input
            className="post-filter-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar"
            autoFocus
            style={{ marginBottom: "6px" }}
          />
          <div style={{ maxHeight: "220px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
            {visibleOptions.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onToggle(option.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    padding: "7px 8px",
                    border: "none",
                    borderRadius: "6px",
                    background: isSelected ? "#eff6ff" : "transparent",
                    color: isSelected ? "#0056c7" : "#334155",
                    cursor: "pointer",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    textAlign: "left",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: "15px",
                      height: "15px",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "4px",
                      border: `1.5px solid ${isSelected ? "#0065DD" : "#cbd5e1"}`,
                      background: isSelected ? "#0065DD" : "#fff",
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 800,
                    }}
                  >
                    {isSelected ? "✓" : ""}
                  </span>
                  <span>{option.label}</span>
                </button>
              );
            })}
            {visibleOptions.length === 0 && (
              <span style={{ padding: "10px 8px", color: "#94a3b8", fontFamily: "Roboto, sans-serif", fontSize: "12px" }}>
                Sin resultados
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TIPO_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  CLIENTE:    { bg: "#ede9fe", color: "#6d28d9", label: "Cliente"    },
  CUENTA:     { bg: "#dbeafe", color: "#1d4ed8", label: "Cuenta"     },
  RECLUTADOR: { bg: "#fef3c7", color: "#b45309", label: "Reclutador" },
};

const PAGE_SIZE_SUGGESTIONS = [10, 20, 50, 100];

// Cada cuánto se consulta si la migración de datos / el cálculo de completitud
// están corriendo en el servidor (timer u otro usuario).
const JOB_STATUS_POLL_MS = 15000;

const EMPTY_FILTERS: ActiveFilters = {
  code: [], formCode: [], formCategory: [], DNI: [], names: [], client: [], business: [],
  vacancy: [], recruiter: [], createdAt: [], status: [], shift: [], position: [], locality: [],
  gender: [], phone: [], cedula: [], abVariant: [], formStage: [], stageWhatsapp: [],
};
const EMPTY_INPUTS: FilterInputs = {
  code: "", formCode: "", formCategory: "", DNI: "", names: "", client: "", business: "",
  vacancy: "", recruiter: "", createdAt: "", status: "", shift: "", position: "", locality: "",
  gender: "", phone: "", cedula: "", abVariant: "", formStage: "", stageWhatsapp: "",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Prefijo de URL para los filtros de esta tabla. Permite que la tabla de
// Formularios (que usa el prefijo `f_`) coexista en la misma URL sin que sus
// filtros se pisen al cambiar de tab.
const URL_PREFIX = "p_";
const SEARCH_URL_PARAM = `${URL_PREFIX}q`;

const parseUrl = (search: string) => {
  const sp = new URLSearchParams(search);
  const filters: ActiveFilters = {
    code: [], formCode: [], formCategory: [], DNI: [], names: [], client: [], business: [],
    vacancy: [], recruiter: [], createdAt: [], status: [], shift: [], position: [], locality: [],
    gender: [], phone: [], cedula: [], abVariant: [], formStage: [], stageWhatsapp: [],
  };
  FILTER_KEYS.forEach((k) => {
    const v = sp.get(`${URL_PREFIX}${k}`);
    if (v) filters[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
  });
  const rawPage  = Number(sp.get(`${URL_PREFIX}page`));
  const rawLimit = sp.get(`${URL_PREFIX}limit`);
  const page  = Number.isFinite(rawPage)  && rawPage  > 0 ? rawPage  : 1;
  const limit = rawLimit === null ? DEFAULT_PAGE_SIZE : parsePageSize(rawLimit);
  const searchTerm = (sp.get(SEARCH_URL_PARAM) ?? "").trim();
  return { filters, search: searchTerm, page, limit };
};

const buildSearch = (
  filters: ActiveFilters,
  page: number,
  limit: number,
  current: string,
) => {
  const sp = new URLSearchParams(current);
  FILTER_KEYS.forEach((k) => {
    sp.delete(`${URL_PREFIX}${k}`);
    if (filters[k].length) sp.set(`${URL_PREFIX}${k}`, filters[k].join(","));
  });
  sp.set(`${URL_PREFIX}page`,  String(page));
  sp.set(`${URL_PREFIX}limit`, String(limit));
  return sp.toString();
};

// El backend serializa createdAt con sufijo "Z" pero el valor ya es hora local
// de Chile (no UTC real). Fijamos timeZone: "UTC" para mostrar el reloj tal cual
// viene, sin que el navegador del usuario lo vuelva a desplazar según su zona
// horaria (esa reconversión hacía que en Chile se viera 4 h menos que el dato).
const dateFormatter = new Intl.DateTimeFormat("es-CL", {
  day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC",
});

const timeFormatter = new Intl.DateTimeFormat("es-CL", {
  hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC",
});

const formatDate = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${dateFormatter.format(d)} ${timeFormatter.format(d)}`;
};

const formatDateParts = (iso: string): { date: string; time: string } | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return { date: dateFormatter.format(d), time: timeFormatter.format(d) };
};

const filtersEqual = (a: ActiveFilters, b: ActiveFilters) =>
  FILTER_KEYS.every(
    (k) =>
      a[k].length === b[k].length &&
      a[k].every((v, i) => v === b[k][i]),
  );

// ─── pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  currentPage:      number;
  totalPages:       number;
  pageSize:         number;
  totalItems:       number;
  onPageChange:     (p: number) => void;
  onPageSizeChange: (s: number) => void;
}

const Pagination = ({
  currentPage, totalPages, pageSize, totalItems,
  onPageChange, onPageSizeChange,
}: PaginationProps) => {
  const [pageSizeInput, setPageSizeInput] = useState<string>(String(pageSize));
  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);

  useEffect(() => {
    setPageSizeInput(String(pageSize));
  }, [pageSize]);

  const commitPageSize = () => {
    const parsed = Number(pageSizeInput);
    if (!Number.isInteger(parsed) || parsed < MIN_PAGE_SIZE || parsed > MAX_PAGE_SIZE) {
      setPageSizeInput(String(pageSize));
      return;
    }
    if (parsed !== pageSize) onPageSizeChange(parsed);
  };

  const pages: (number | "…")[] = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const result: (number | "…")[] = [1];
    if (currentPage > 3) result.push("…");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      result.push(i);
    }
    if (currentPage < totalPages - 2) result.push("…");
    if (totalPages > 1) result.push(totalPages);
    return result;
  }, [currentPage, totalPages]);

  const btnBase: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "34px", height: "34px", borderRadius: "8px",
    border: "1px solid #e2e8f0", background: "#fff",
    fontFamily: "Roboto, sans-serif", fontSize: "13px",
    cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", padding: "14px 2px 4px" }}>
      <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", whiteSpace: "nowrap" }}>
        {totalItems === 0
          ? "Sin resultados"
          : `Mostrando ${start}–${end} de ${totalItems} registro${totalItems !== 1 ? "s" : ""}`}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          style={{ ...btnBase, color: currentPage <= 1 ? "#cbd5e1" : "#0065DD", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}
        >
          <KeyboardArrowLeftIcon style={{ fontSize: 18 }} />
        </button>

        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} style={{ width: "34px", textAlign: "center", color: "#94a3b8", fontFamily: "Roboto, sans-serif", fontSize: "13px", userSelect: "none" }}>···</span>
          ) : (
            <motion.button
              key={p}
              onClick={() => onPageChange(p as number)}
              whileTap={{ scale: 0.9 }}
              style={{
                ...btnBase,
                border: p === currentPage ? "none" : "1px solid #e2e8f0",
                background: p === currentPage ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)" : "#fff",
                color: p === currentPage ? "#fff" : "#475569",
                fontWeight: p === currentPage ? 600 : 400,
                boxShadow: p === currentPage ? "0 2px 8px rgba(0,101,221,0.28)" : "none",
              }}
            >
              {p}
            </motion.button>
          ),
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || totalPages === 0}
          style={{ ...btnBase, color: currentPage >= totalPages || totalPages === 0 ? "#cbd5e1" : "#0065DD", cursor: currentPage >= totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}
        >
          <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", whiteSpace: "nowrap" }}>Por página</span>
        <input
          type="number"
          min={MIN_PAGE_SIZE}
          max={MAX_PAGE_SIZE}
          step={1}
          value={pageSizeInput}
          list="postulaciones-page-size-options"
          aria-label="Registros por página"
          title={`Ingresa un valor entre ${MIN_PAGE_SIZE} y ${MAX_PAGE_SIZE}`}
          onChange={(e) => setPageSizeInput(e.target.value)}
          onBlur={commitPageSize}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitPageSize();
              e.currentTarget.blur();
            }
          }}
          style={{
            width: "82px", padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: "8px",
            fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#334155",
            background: "#fff", outline: "none",
          }}
        />
        <datalist id="postulaciones-page-size-options">
          {PAGE_SIZE_SUGGESTIONS.map((size) => <option key={size} value={size} />)}
        </datalist>
      </div>
    </div>
  );
};

// ─── modal types ──────────────────────────────────────────────────────────────

interface IApplicationDetail {
  applicationId: string;
  code: string;
  formType: string;
  formCode: string;
  createdAt: string;
  client: string;
  businessArea: string;
  recruiter: string;
  vacancy: string;
  personId: string;
  DNI: string;
  names: string;
  lastnames: string;
  birthdate: string;
  gender: string;
  /** Id del género, para preseleccionar el select en el modal de validación. */
  generoId?: string | null;
  maritalStatus: string;
  nationality: string;
  /** Id de la nacionalidad (país), para preseleccionar el select. */
  nacionalidadId?: number | null;
  visaType: string;
  email: string;
  phone: string;
  address: string;
  locality: string;
  children: number;
  healthSystem: string;
  afp: string;
  bank: string;
  accountType: string;
  accountNumber: string;
  tshirtSize: string;
  pantSize: string;
  shoeSize: string;
  availability: string;
  shift?: string;
  position?: string;
  /** Veredicto de cédula actual (para el estado y el gate de validación manual). */
  veredictoCedula?: string | null;
  /** Campos que discrepan (solo con requiere_revision). */
  detalleCedula?: IDetalleCedulaItem[] | null;
  hasDisease: boolean;
  disease: string;
  hasDisability: boolean;
  disability: string;
  emergencyContact: { name?: string; phone?: string; relationship?: string };
  documents: {
    personDocumentId: string;
    documentType: string;
    originalFileName: string;
    mimeType: string;
    extension: string;
    size: number;
    blobUrl: string;
    sasUrl?: string;
    uploadedAt: string;
  }[];
}

// ─── modal helpers ─────────────────────────────────────────────────────────────

const MODAL_FONT = "'Inter', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Colores e ícono (Material Symbols, relleno) de cada sección del modal.
const SECTION_COLORS: Record<string, { accent: string; bg: string; border: string; icon: string }> = {
  "Postulación":              { accent: "#1d6fe0", bg: "#eef5ff", border: "#d6e6fb", icon: "description" },
  "Datos personales":         { accent: "#6d28d9", bg: "#f3efff", border: "#e4dcfb", icon: "person" },
  "Contacto":                 { accent: "#0e7490", bg: "#ecfbfd", border: "#c7eef3", icon: "call" },
  "Datos bancarios y salud":  { accent: "#059669", bg: "#ecfdf5", border: "#c8eedd", icon: "account_balance" },
  "Tallas":                   { accent: "#d97706", bg: "#fff8eb", border: "#f8e3b5", icon: "apparel" },
  "Contacto de emergencia":   { accent: "#475569", bg: "#f4f6f9", border: "#e2e8f0", icon: "call" },
  "Documentos":               { accent: "#1d6fe0", bg: "#eef5ff", border: "#d6e6fb", icon: "attach_file" },
  "Observación":              { accent: "#4338ca", bg: "#eef2ff", border: "#dcdffb", icon: "edit_note" },
  "Historial de validación":  { accent: "#4338ca", bg: "#eef2ff", border: "#dcdffb", icon: "history" },
};

// Estilos compartidos del modal de postulación: dos columnas de tarjetas (una
// en pantallas angostas), separadores entre filas y entre documentos.
const POSTULACION_MODAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
.pm-body { display: flex; flex-direction: column; gap: 16px; }
.pm-cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: start; }
.pm-stack { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.pm-kv-row + .pm-kv-row { border-top: 1px solid #eef2f6; }
.pm-doclist > * + * { border-top: 1px solid #eef2f6; }
.pm-btn-volver { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px 8px 12px; border-radius: 9px; border: 1px solid #c7d2fe; background: #eef2ff; color: #4338ca; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, color 0.15s; }
.pm-btn-volver:hover:not(:disabled) { background: #e0e7ff; border-color: #a5b4fc; color: #3730a3; box-shadow: 0 2px 8px rgba(67,56,202,0.18); }
.pm-btn-volver:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }
.pm-btn-volver:disabled { opacity: 0.55; cursor: not-allowed; }
.pm-btn-volver__icono { font-size: 18px; line-height: 1; transition: transform 0.15s; }
.pm-btn-volver:hover:not(:disabled) .pm-btn-volver__icono { transform: translateX(-3px); }
@media (max-width: 960px) { .pm-cols { grid-template-columns: minmax(0, 1fr); } }
`;

/**
 * Tarjeta de sección: encabezado teñido con ícono relleno y título en
 * mayúsculas del color de la sección, y un `aside` opcional a la derecha.
 */
const ModalSection = ({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) => {
  const cfg = SECTION_COLORS[title] ?? { accent: "#1d6fe0", bg: "#eef5ff", border: "#d6e6fb", icon: "info" };
  return (
    <section style={{ borderRadius: "12px", border: `1px solid ${cfg.border}`, background: "#fff", overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
      <div style={{ background: cfg.bg, padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, color: cfg.accent }}>
          <span className="material-symbols-outlined" style={{ fontSize: "22px", lineHeight: 1, fontVariationSettings: "'FILL' 1" }}>{cfg.icon}</span>
          <h3 style={{ margin: 0, fontFamily: MODAL_FONT, fontSize: "14.5px", fontWeight: 700, color: cfg.accent, textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</h3>
        </div>
        {aside}
      </div>
      <div style={{ padding: "4px 20px 8px" }}>{children}</div>
    </section>
  );
};

/**
 * Fila etiqueta (izquierda) / valor (derecha). Con `children` muestra un control
 * en lugar del valor (modo edición).
 */
const KvRow = ({ label, value, children }: { label: string; value?: string | number | null; children?: ReactNode }) => {
  const empty = value == null || value === "";
  return (
    <div className="pm-kv-row" style={{ display: "grid", gridTemplateColumns: "minmax(120px, 3fr) minmax(0, 2fr)", columnGap: "16px", alignItems: "center", padding: children ? "6px 0" : "6px 0", minHeight: "22px" }}>
      <div style={{ fontFamily: MODAL_FONT, fontSize: "13.5px", fontWeight: 400, color: "#475569", lineHeight: 1.4 }}>{label}</div>
      {children ?? (
        <div style={{ fontFamily: MODAL_FONT, fontSize: "13.5px", fontWeight: 500, color: empty ? "#94a3b8" : "#0f172a", lineHeight: 1.4, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
          {empty ? "—" : value}
        </div>
      )}
    </div>
  );
};

const formatBytes = (bytes: number) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const calculateAge = (birthdate: string): number | null => {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

// ─── historial de validación manual ────────────────────────────────────────────

interface IValidationHistoryItem {
  id: string;
  codigo: string | null;
  accion: string;
  veredicto: string;
  cambios: { campo: string; valorAnterior: string | null; valorNuevo: string | null }[];
  documentos: { tipo: string; personDocumentId: string | null; nombreArchivo: string | null }[];
  comentario: string;
  usuarioId: string | null;
  usuarioNombre: string | null;
  fechaCreacion: string;
}

/**
 * Historial de las validaciones manuales de una persona: quién intervino, cuándo,
 * qué cambió (con valor anterior/nuevo), qué documentos reemplazó y su observación.
 * Se alimenta por personId (no por postulación), porque los datos viven en la
 * persona. Con `hideWhenEmpty` no dibuja nada si no hay intervenciones (útil en el
 * modal de vista, para no ensuciar los registros que nunca pasaron por acá).
 */
const HistorialValidacion = ({ personId, hideWhenEmpty }: { personId: string; hideWhenEmpty?: boolean }) => {
  const [items, setItems] = useState<IValidationHistoryItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl || !personId) { setItems([]); return; }
    let cancelled = false;
    fetch(`${baseUrl}/application/validation-history?personId=${encodeURIComponent(personId)}`)
      .then(async (res) => { if (!res.ok) throw new Error(await readApiErrorMessage(res)); return res.json(); })
      .then((data) => { if (!cancelled) setItems(Array.isArray(data?.history) ? data.history : []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [personId]);

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const resumen = (h: IValidationHistoryItem): string => {
    const nombre = h.usuarioNombre || "Un usuario";
    if (h.accion === "aprobado_sin_cambios") return `${nombre} aprobó el registro sin modificaciones`;
    const partes: string[] = [];
    if (h.cambios?.length) partes.push(`${h.cambios.length} dato(s)`);
    if (h.documentos?.length) partes.push(`${h.documentos.length} documento(s)`);
    return `${nombre} modificó ${partes.join(" y ") || "el registro"}`;
  };

  if (failed) {
    if (hideWhenEmpty) return null;
    return (
      <ModalSection title="Historial de validación">
        <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#b91c1c" }}>No se pudo cargar el historial.</div>
      </ModalSection>
    );
  }
  if (items === null) {
    if (hideWhenEmpty) return null;
    return (
      <ModalSection title="Historial de validación">
        <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#94a3b8" }}>Cargando historial…</div>
      </ModalSection>
    );
  }
  if (items.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <ModalSection title="Historial de validación">
        <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#94a3b8", textAlign: "center", padding: "8px 0" }}>
          Sin intervenciones manuales registradas.
        </div>
      </ModalSection>
    );
  }

  return (
    <ModalSection title="Historial de validación">
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {items.map((h) => {
          const esModificado = h.accion === "modificado";
          return (
            <div key={h.id} style={{ borderRadius: "10px", border: "1px solid #e2e8f0", background: "#f8fafc", padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", fontWeight: 600, color: "#1e293b" }}>{resumen(h)}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: "10px", background: esModificado ? "#eef2ff" : "#f1f5f9", color: esModificado ? "#4338ca" : "#475569", fontSize: "10.5px", fontWeight: 600, fontFamily: MODAL_FONT, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {esModificado ? "Modificado" : "Aprobado"}
                  </span>
                  <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#94a3b8" }}>{fmt(h.fechaCreacion)}</span>
                </span>
              </div>

              {Array.isArray(h.cambios) && h.cambios.length > 0 && (
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {h.cambios.map((c, i) => (
                    <div key={i} style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#475569" }}>
                      · <b>{c.campo}</b>: <span style={{ color: "#94a3b8", textDecoration: "line-through" }}>{c.valorAnterior ?? "—"}</span> → <span style={{ color: "#1e293b", fontWeight: 600 }}>{c.valorNuevo ?? "—"}</span>
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(h.documentos) && h.documentos.length > 0 && (
                <div style={{ marginTop: "6px", fontFamily: MODAL_FONT, fontSize: "12px", color: "#475569" }}>
                  Documentos reemplazados: {h.documentos.map((d) => etiquetaDocumento(d.tipo)).join(", ")}
                </div>
              )}

              {h.comentario && (
                <div style={{ marginTop: "8px", padding: "7px 10px", borderRadius: "8px", background: "#fff", border: "1px solid #e2e8f0", fontFamily: MODAL_FONT, fontSize: "12px", color: "#334155" }}>
                  <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "3px" }}>Observación</span>
                  {h.comentario}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModalSection>
  );
};

// ─── validación manual: helpers ───────────────────────────────────────────────

/**
 * Estados de cédula en los que un reclutador puede intervenir manualmente:
 * discrepancia de datos, foto ilegible, o uno ya validado a mano (que puede
 * corregirse otra vez). En cualquier otro estado la acción no se ofrece.
 */
const ESTADOS_VALIDACION_MANUAL = new Set(["requiere_revision", "requiere_recaptura", "validado_manual"]);

const puedeValidarManualmente = (veredicto: string | null | undefined): boolean =>
  ESTADOS_VALIDACION_MANUAL.has(String(veredicto ?? ""));

const DOCUMENTOS_EDITABLES: { type: string; label: string }[] = [
  { type: "cedulaFrontal", label: "Cédula (frontal)" },
  { type: "cedulaTrasera", label: "Cédula (trasera)" },
  { type: "curriculum",    label: "Currículum"       },
];

// Etiqueta legible de un tipo de documento; los tipos no conocidos se pasan de
// camelCase a texto ("certificadoAntecedentes" → "Certificado antecedentes").
const etiquetaDocumento = (type: string): string => {
  const conocido = DOCUMENTOS_EDITABLES.find((d) => d.type === type)?.label;
  if (conocido) return conocido;
  const texto = (type ?? "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : "Documento";
};

interface ICampoCambio { campo: string; valorAnterior: string | null; valorNuevo: string | null }

const editInputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontFamily: MODAL_FONT,
  fontSize: "13px",
  color: "#1e293b",
  outline: "none",
};

// ─── modal ────────────────────────────────────────────────────────────────────

/**
 * Modal único de la postulación: muestra la ficha completa y, cuando el
 * veredicto de cédula admite intervención manual, ofrece "Validar datos", que
 * pasa el mismo modal a modo edición (datos de identidad, reemplazo/rotación de
 * documentos y observación obligatoria). Así el reclutador revisa y corrige
 * desde un solo lugar.
 */
const PostulacionModal = ({
  postulacion,
  onClose,
  onValidated,
}: {
  postulacion: IApplicationApi;
  onClose: () => void;
  onValidated: () => void;
}) => {
  const [detail, setDetail]         = useState<IApplicationDetail | null>(null);
  const [loading, setLoading]       = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [genders, setGenders]       = useState<SearchSelectOption[]>([]);
  const [nationalities, setNationalities] = useState<SearchSelectOption[]>([]);
  // countryId → ISO alfa-3 ("CHL"), para mapear la nacionalidad que lee la IA.
  const [paisAlfa3, setPaisAlfa3] = useState<Record<string, string>>({});
  // Un solo visor abierto a la vez.
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);

  // Solo se puede pasar a edición cuando el veredicto admite intervención manual.
  const canValidate = puedeValidarManualmente(postulacion.veredictoCedula);
  const [editing, setEditing] = useState(false);

  // Campos editables.
  const [names, setNames]           = useState("");
  const [lastnames, setLastnames]   = useState("");
  const [birthdate, setBirthdate]   = useState("");
  const [generoId, setGeneroId]     = useState("");
  const [nacionalidadId, setNacionalidadId] = useState("");
  const [newFiles, setNewFiles]     = useState<Record<string, File | null>>({});
  // Giro pendiente por tipo de documento (se guarda al validar) y versión del
  // archivo para recargar el preview una vez guardado.
  const [rotaciones, setRotaciones] = useState<Record<string, number>>({});
  const [versionesDoc, setVersionesDoc] = useState<Record<string, number>>({});

  const [comment, setComment]           = useState("");
  const [commentTouched, setCommentTouched] = useState(false);
  // Última observación generada por el sistema (ver sincronizarObservacion).
  const observacionGeneradaRef = useRef("");
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState<string | null>(null);
  // Salida pedida con cambios sin guardar: a la vista de lectura o cerrar el
  // modal. Mientras no sea null se muestra el diálogo de confirmación.
  const [salidaPendiente, setSalidaPendiente] = useState<"vista" | "cerrar" | null>(null);
  // Resultado del último "Aplicar sugerencias de IA" (qué se aplicó y qué no).
  const [resultadoIA, setResultadoIA]   = useState<{ aplicadas: ISugerenciaAplicada[]; omitidas: ISugerenciaOmitida[] } | null>(null);

  // Deja los campos editables iguales al detalle y descarta cambios pendientes.
  const resetEdition = useCallback((data: IApplicationDetail) => {
    setNames(data.names ?? "");
    setLastnames(data.lastnames ?? "");
    setBirthdate(data.birthdate ?? "");
    setGeneroId(data.generoId != null ? String(data.generoId) : "");
    setNacionalidadId(data.nacionalidadId != null ? String(data.nacionalidadId) : "");
    setNewFiles({});
    setRotaciones({});
    setComment("");
    setCommentTouched(false);
    observacionGeneradaRef.current = "";
    setSubmitError(null);
    setResultadoIA(null);
  }, []);

  // ── carga: detalle + catálogos (estos solo si se puede validar) ──
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl || !postulacion.code) { setLoading(false); return; }

    let cancelled = false;
    Promise.all([
      fetch(`${baseUrl}/application/detail/${encodeURIComponent(postulacion.code)}`).then(async (res) => {
        if (!res.ok) throw new Error(await readApiErrorMessage(res));
        return (await res.json()) as IApplicationDetail;
      }),
      canValidate
        ? fetch(`${baseUrl}/catalogs/identification`).then((res) => (res.ok ? res.json() : { genders: [], nationalities: [] })).catch(() => ({ genders: [], nationalities: [] }))
        : Promise.resolve({ genders: [], nationalities: [] }),
    ])
      .then(([data, catalogs]) => {
        if (cancelled) return;
        setDetail(data);
        resetEdition(data);
        setGenders(Array.isArray(catalogs?.genders) ? catalogs.genders.map((g: any) => ({ code: String(g.genderId), name: g.name })) : []);
        setNationalities(Array.isArray(catalogs?.nationalities) ? catalogs.nationalities.map((n: any) => ({ code: String(n.countryId), name: n.name })) : []);
        setPaisAlfa3(Array.isArray(catalogs?.nationalities)
          ? Object.fromEntries(catalogs.nationalities.filter((n: any) => n.code).map((n: any) => [String(n.countryId), String(n.code)]))
          : {});
        setFetchError(null);
      })
      .catch(() => { if (!cancelled) setFetchError("No se pudo cargar el detalle de la postulación."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [postulacion.code, canValidate, resetEdition]);

  // Nombre del género/nacionalidad a partir de su id (para el texto de cambios).
  const genderName = (id: string): string => genders.find((g) => g.code === id)?.name ?? (id || "—");
  const nationalityName = (id: string): string => nationalities.find((n) => n.code === id)?.name ?? (id || "—");

  // ── diferencias respecto al detalle original ──
  const fieldChanges: ICampoCambio[] = useMemo(() => {
    if (!detail || !editing) return [];
    const changes: ICampoCambio[] = [];
    const norm = (v: string) => (v ?? "").trim();
    if (norm(names) !== norm(detail.names)) changes.push({ campo: "Nombres", valorAnterior: detail.names || "—", valorNuevo: names || "—" });
    if (norm(lastnames) !== norm(detail.lastnames)) changes.push({ campo: "Apellidos", valorAnterior: detail.lastnames || "—", valorNuevo: lastnames || "—" });
    if (norm(birthdate) !== norm(detail.birthdate)) changes.push({ campo: "Fecha de nacimiento", valorAnterior: detail.birthdate || "—", valorNuevo: birthdate || "—" });
    const currentGenero = detail.generoId != null ? String(detail.generoId) : "";
    if (generoId !== currentGenero) changes.push({ campo: "Género", valorAnterior: genderName(currentGenero), valorNuevo: genderName(generoId) });
    const currentNac = detail.nacionalidadId != null ? String(detail.nacionalidadId) : "";
    if (nacionalidadId !== currentNac) changes.push({ campo: "Nacionalidad", valorAnterior: nationalityName(currentNac), valorNuevo: nationalityName(nacionalidadId) });
    return changes;
    // Dependencias listadas a proposito (todas las usadas en el calculo).
  }, [detail, editing, names, lastnames, birthdate, generoId, nacionalidadId, genders, nationalities]);

  const docChanges = useMemo(
    () => Object.entries(newFiles).filter(([, f]) => Boolean(f)).map(([type]) => ({ type, label: etiquetaDocumento(type) })),
    [newFiles],
  );

  // Rotaciones pendientes sobre documentos existentes. Si el documento además se
  // reemplaza, manda el reemplazo (la rotación se descarta).
  const rotChanges = useMemo(() => {
    if (!detail) return [];
    return Object.entries(rotaciones)
      .map(([type, giro]) => ({ type, giro: normalizarGiro(giro), doc: detail.documents.find((d) => d.documentType === type) }))
      .filter((r) => r.giro !== 0 && r.doc && !newFiles[r.type])
      .map((r) => ({ ...r, doc: r.doc!, label: etiquetaDocumento(r.type) }));
  }, [rotaciones, newFiles, detail]);

  const hasChanges = fieldChanges.length > 0 || docChanges.length > 0 || rotChanges.length > 0;

  // Observación autogenerada: "Aprobado sin cambios." o el resumen de los
  // cambios. El usuario puede editarla; lo generado se mantiene al día con los
  // datos (sincronizarObservacion), así nunca se aprueba sin observación.
  const suggestion = useMemo(() => {
    const parts: string[] = [];
    if (fieldChanges.length) parts.push("Datos: " + fieldChanges.map((c) => `${c.campo} ('${c.valorAnterior ?? "—"}' → '${c.valorNuevo ?? "—"}')`).join("; "));
    if (docChanges.length) parts.push("Documentos reemplazados: " + docChanges.map((d) => d.label).join(", "));
    if (rotChanges.length) parts.push("Documentos rotados: " + rotChanges.map((r) => `${r.label} (${r.giro}°)`).join(", "));
    return parts.length ? `Validación manual. ${parts.join(". ")}.` : OBSERVACION_SIN_CAMBIOS;
  }, [fieldChanges, docChanges, rotChanges]);

  useEffect(() => {
    if (!editing) return;
    const generadaAnterior = observacionGeneradaRef.current;
    observacionGeneradaRef.current = suggestion;
    setComment((actual) => sincronizarObservacion({
      actual,
      generadaAnterior,
      generadaNueva: suggestion,
      editadaPorUsuario: commentTouched,
    }));
  }, [suggestion, commentTouched, editing]);

  const estadoCfg = resolveEstado(postulacion.computedStatus);
  const estadoMsg = buildStatusMessage(postulacion);
  const tipoCfg   = TIPO_CONFIG[postulacion.formType] ?? { bg: "#f1f5f9", color: "#475569", label: postulacion.formType || "—" };
  const initials  = postulacion.names.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? "").join("") || "—";

  // Siempre se puede validar: si la observación quedó vacía se envía la
  // generada ("Aprobado sin cambios." o el resumen de cambios).
  const canSubmit = editing && !loading && !fetchError && !submitting;
  const observacionAEnviar = comment.trim() || suggestion;

  const startEditing = () => {
    if (!detail) return;
    resetEdition(detail);
    setViewingDocId(null);
    setEditing(true);
  };

  /**
   * Inserta en los campos lo que leyó la IA del documento (ver sugerenciasIA).
   * Si no se estaba editando, entra en edición partiendo de la ficha. La
   * observación se autocompleta solo si el usuario no escribió nada propio.
   */
  const aplicarSugerencias = () => {
    if (!detail || !Array.isArray(detail.detalleCedula) || detail.detalleCedula.length === 0) return;
    const partida = editing
      ? { names, lastnames, birthdate, generoId, nacionalidadId }
      : {
          names: detail.names ?? "",
          lastnames: detail.lastnames ?? "",
          birthdate: detail.birthdate ?? "",
          generoId: detail.generoId != null ? String(detail.generoId) : "",
          nacionalidadId: detail.nacionalidadId != null ? String(detail.nacionalidadId) : "",
        };
    const sinTextoPropio = !editing || !commentTouched || comment.trim() === "";
    if (!editing) startEditing();

    const r = aplicarSugerenciasIA(detail.detalleCedula, partida, {
      genders,
      nationalities: nationalities.map((n) => ({ ...n, alfa3: paisAlfa3[n.code] })),
    });
    setNames(r.estado.names);
    setLastnames(r.estado.lastnames);
    setBirthdate(r.estado.birthdate);
    setGeneroId(r.estado.generoId);
    setNacionalidadId(r.estado.nacionalidadId);
    setResultadoIA({ aplicadas: r.aplicadas, omitidas: r.omitidas });
    if (sinTextoPropio && r.aplicadas.length > 0) {
      setComment(comentarioSugerenciasIA(r.aplicadas, r.omitidas));
      setCommentTouched(true);
    }
  };

  const cancelEditing = () => {
    if (submitting) return;
    if (detail) resetEdition(detail);
    setViewingDocId(null);
    setEditing(false);
  };

  // Algo que se perdería al salir de la edición: datos, documentos, rotaciones
  // o una observación escrita a mano.
  const cambiosPendientes = editing && (hasChanges || (commentTouched && comment.trim() !== ""));

  const salir = (destino: "vista" | "cerrar") => (destino === "vista" ? cancelEditing() : onClose());

  /** Volver a la vista o cerrar: si hay cambios sin guardar, primero pregunta. */
  const pedirSalida = (destino: "vista" | "cerrar") => {
    if (submitting) return;
    if (cambiosPendientes) { setSalidaPendiente(destino); return; }
    salir(destino);
  };

  const descartarYSalir = () => {
    const destino = salidaPendiente;
    setSalidaPendiente(null);
    if (destino) salir(destino);
  };

  const guardarDesdeDialogo = () => {
    setSalidaPendiente(null);
    handleSubmit();
  };

  const handleBackdropClick = () => pedirSalida("cerrar");

  // Esc: cierra el diálogo de confirmación si está abierto; si no, se comporta
  // como la X (con cambios sin guardar, primero pregunta).
  useEscapeKey(() => {
    if (submitting) return;
    if (salidaPendiente) { setSalidaPendiente(null); return; }
    pedirSalida("cerrar");
  });

  const handleSubmit = async () => {
    if (!detail || !canSubmit) return;
    setSubmitError(null);

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    const userId  = sessionStorage.getItem("usuarioId") ?? "";
    if (!baseUrl) { setSubmitError("Configuración faltante: REACT_APP_API_APPLICANT"); return; }

    const action = hasChanges ? "modificado" : "aprobado_sin_cambios";

    const fields: Record<string, string | number> = {};
    const currentGenero = detail.generoId != null ? String(detail.generoId) : "";
    const currentNac = detail.nacionalidadId != null ? String(detail.nacionalidadId) : "";
    if ((names ?? "").trim() !== (detail.names ?? "").trim()) fields.names = names.trim();
    if ((lastnames ?? "").trim() !== (detail.lastnames ?? "").trim()) fields.lastnames = lastnames.trim();
    if ((birthdate ?? "").trim() !== (detail.birthdate ?? "").trim()) fields.birthdate = birthdate.trim();
    if (generoId !== currentGenero && generoId) fields.generoId = generoId;
    if (nacionalidadId !== currentNac && nacionalidadId) fields.nacionalidadId = Number(nacionalidadId);

    const fd = new FormData();
    fd.append("payload", JSON.stringify({
      personId: detail.personId,
      action,
      comment: observacionAEnviar,
      fields,
      changes: fieldChanges,
    }));
    Object.entries(newFiles).forEach(([type, file]) => {
      if (file) { fd.append("files", file); fd.append("fileDocumentTypes", type); }
    });

    setSubmitting(true);
    try {
      // Primero se guardan las rotaciones: cada una que se aplica se limpia del
      // estado, así un reintento (si falla la validación) no la vuelve a girar.
      for (const r of rotChanges) {
        try {
          const { size } = await rotarDocumento(r.doc, r.giro);
          setRotaciones((prev) => ({ ...prev, [r.type]: 0 }));
          setVersionesDoc((prev) => ({ ...prev, [r.type]: (prev[r.type] ?? 0) + 1 }));
          if (size) setDetail((prev) => prev && ({ ...prev, documents: prev.documents.map((d) => (d.personDocumentId === r.doc.personDocumentId ? { ...d, size } : d)) }));
        } catch (err) {
          throw new Error(`${r.label}: ${(err as Error).message}`);
        }
      }

      const res = await fetch(
        `${baseUrl}/application/validate-manual/${encodeURIComponent(postulacion.code)}?userId=${encodeURIComponent(userId)}`,
        { method: "POST", body: fd },
      );
      if (!res.ok) throw new Error(await readApiErrorMessage(res));
      onValidated();
      onClose();
    } catch (err) {
      setSubmitError(getUserErrorMessage(err as Error, "No se pudo validar el registro."));
    } finally {
      setSubmitting(false);
    }
  };

  // Al rotar desde el visor en modo vista, la rotación se guarda al tiro; acá
  // solo se refleja el nuevo tamaño del archivo.
  const handleRotadoVista = (personDocumentId: string) => ({ size }: { size: number }) => setDetail((prev) => prev && ({
    ...prev,
    documents: prev.documents.map((d) => (d.personDocumentId === personDocumentId && size ? { ...d, size } : d)),
  }));

  // Fila de documento: nombre del archivo como título y "tipo · formato · peso"
  // como detalle, igual en vista y en edición.
  const docSubtitle = (doc: IApplicationDetail["documents"][number]) =>
    `${etiquetaDocumento(doc.documentType)} · ${(doc.extension ?? "").toUpperCase()} · ${formatBytes(doc.size)}`;

  // Documento en modo vista (también se usa en edición para los tipos que no se
  // pueden reemplazar desde acá).
  const renderDocumentoVista = (doc: IApplicationDetail["documents"][number]) => (
    <DocumentoConVisor
      key={doc.personDocumentId}
      variante="lista"
      doc={doc}
      subtitle={docSubtitle(doc)}
      open={viewingDocId === doc.personDocumentId}
      onToggle={() => setViewingDocId((id) => (id === doc.personDocumentId ? null : doc.personDocumentId))}
      versionArchivo={versionesDoc[doc.documentType] ?? 0}
      onRotado={handleRotadoVista(doc.personDocumentId)}
    />
  );

  const renderDocumentosEdicion = (d: IApplicationDetail) => {
    const tiposEditables = new Set(DOCUMENTOS_EDITABLES.map((x) => x.type));
    const otros = d.documents.filter((doc) => !tiposEditables.has(doc.documentType));
    return (
      <div className="pm-doclist">
        {DOCUMENTOS_EDITABLES.map(({ type, label }) => {
          const actual = d.documents.find((doc) => doc.documentType === type);
          const nuevo = newFiles[type];
          const replaceControls = (
            <>
              {nuevo && (
                <button onClick={() => setNewFiles((prev) => ({ ...prev, [type]: null }))} style={{ padding: "5px 10px", borderRadius: "7px", border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontFamily: MODAL_FONT, fontSize: "12px", cursor: "pointer" }}>
                  Quitar
                </button>
              )}
              <label style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: "7px", border: "1px solid #c7d2fe", background: "#eef2ff", color: "#4338ca", fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}>
                {actual || nuevo ? "Reemplazar" : "Subir"}
                <input
                  type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setNewFiles((prev) => ({ ...prev, [type]: f }));
                    if (f) setRotaciones((prev) => ({ ...prev, [type]: 0 }));
                    e.target.value = "";
                  }}
                />
              </label>
            </>
          );

          // Con documento existente: visor + controles de reemplazo. La rotación
          // queda pendiente y se guarda junto con "Guardar y validar".
          if (actual) {
            return (
              <DocumentoConVisor
                key={type}
                variante="lista"
                doc={actual}
                subtitle={nuevo ? `${label} · Reemplazo pendiente: ${nuevo.name}` : docSubtitle(actual)}
                extraActions={replaceControls}
                open={viewingDocId === actual.personDocumentId}
                onToggle={() => setViewingDocId((id) => (id === actual.personDocumentId ? null : actual.personDocumentId))}
                versionArchivo={versionesDoc[type] ?? 0}
                permitirRotar={!nuevo}
                rotacionPendiente={rotaciones[type] ?? 0}
                onRotacionPendienteChange={(giro: number) => setRotaciones((prev) => ({ ...prev, [type]: giro }))}
              />
            );
          }

          // Sin documento aún: solo permitir subir (nada que visualizar).
          return (
            <div key={type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "9px 10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: "22px", lineHeight: 1, color: "#94a3b8" }}>upload_file</span>
                <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: "14px", minWidth: 0 }}>
                  <span style={{ fontFamily: MODAL_FONT, fontSize: "14px", fontWeight: 500, color: "#0f172a" }}>{label}</span>
                  <span style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: nuevo ? "#4338ca" : "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "460px" }}>
                    {nuevo ? `Nuevo: ${nuevo.name}` : "Sin documento"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                {replaceControls}
              </div>
            </div>
          );
        })}
        {otros.map(renderDocumentoVista)}
      </div>
    );
  };

  const hayDiscrepancias = Boolean(detail && Array.isArray(detail.detalleCedula) && detail.detalleCedula.length > 0);
  // La validación por IA pidió revisión pero no dejó qué campo discrepa. Pasa
  // con el flujo de n8n cuando lee la cédula con confianza "media" aunque todos
  // los datos coincidan: no hay diferencia que mostrar, solo una lectura dudosa.
  const revisionSinDetalle = Boolean(detail) && !hayDiscrepancias && postulacion.veredictoCedula === "requiere_revision";
  const edad = detail?.birthdate ? calculateAge(detail.birthdate) : null;

  // ── secciones ──
  const seccionPostulacion = detail && (
    <ModalSection title="Postulación">
      <KvRow label="Código"          value={detail.code} />
      <KvRow label="Formulario"      value={detail.formCode} />
      <KvRow label="Fecha"           value={formatDate(detail.createdAt)} />
      <KvRow label="Cliente"         value={detail.client} />
      <KvRow label="Área de negocio" value={detail.businessArea} />
      <KvRow label="Reclutador"      value={detail.recruiter} />
      <KvRow label="Disponibilidad"  value={detail.availability} />
      <KvRow label="Proceso"         value={detail.vacancy} />
      <KvRow label="Cargo"           value={detail.position} />
      <KvRow label="Turno"           value={detail.shift} />
    </ModalSection>
  );

  const seccionDatosPersonales = detail && (
    <ModalSection title="Datos personales">
      {/* RUT: solo lectura (llave de identidad; los cambios de RUT van por mesa de ayuda). */}
      <KvRow label="RUT" value={detail.DNI} />
      {editing ? (
        <>
          <KvRow label="Nombres">
            <input style={editInputStyle} value={names} onChange={(e) => setNames(e.target.value)} />
          </KvRow>
          <KvRow label="Apellidos">
            <input style={editInputStyle} value={lastnames} onChange={(e) => setLastnames(e.target.value)} />
          </KvRow>
          <KvRow label="Fecha de nacimiento">
            <input type="date" style={editInputStyle} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
          </KvRow>
          <KvRow label="Género">
            <SearchSelect value={generoId} options={genders} onChange={setGeneroId} placeholder="Selecciona…" ariaLabel="Género" />
          </KvRow>
          <KvRow label="Estado civil" value={detail.maritalStatus} />
          <KvRow label="Nacionalidad">
            <SearchSelect value={nacionalidadId} options={nationalities} onChange={setNacionalidadId} placeholder="Selecciona…" ariaLabel="Nacionalidad" />
          </KvRow>
        </>
      ) : (
        <>
          <KvRow label="Nombres"             value={detail.names} />
          <KvRow label="Apellidos"           value={detail.lastnames} />
          <KvRow label="Fecha de nacimiento" value={detail.birthdate ? `${detail.birthdate}${edad !== null ? `  (${edad} años)` : ""}` : undefined} />
          <KvRow label="Género"              value={detail.gender} />
          <KvRow label="Estado civil"        value={detail.maritalStatus} />
          <KvRow label="Nacionalidad"        value={detail.nationality ? String(detail.nationality) : undefined} />
        </>
      )}
      <KvRow label="Tipo de visa" value={detail.visaType} />
      <KvRow label="Hijos"        value={detail.children != null ? String(detail.children) : undefined} />
    </ModalSection>
  );

  const seccionContacto = detail && (
    <ModalSection title="Contacto">
      <KvRow label="Email"     value={detail.email} />
      <KvRow label="Teléfono"  value={detail.phone} />
      <KvRow label="Dirección" value={detail.address} />
      <KvRow label="Localidad" value={detail.locality ? String(detail.locality) : undefined} />
    </ModalSection>
  );

  const seccionBancariosSalud = detail && (
    <ModalSection title="Datos bancarios y salud">
      <KvRow label="Sistema de salud" value={detail.healthSystem} />
      <KvRow label="AFP"              value={detail.afp} />
      <KvRow label="Banco"            value={detail.bank} />
      <KvRow label="Tipo de cuenta"   value={detail.accountType} />
      <KvRow label="N° cuenta"        value={detail.accountNumber} />
      {detail.hasDisease    ? <KvRow label="Enfermedad"   value={detail.disease || "Sí"} /> : null}
      {detail.hasDisability ? <KvRow label="Discapacidad" value={detail.disability || "Sí"} /> : null}
    </ModalSection>
  );

  const seccionTallas = detail && (
    <ModalSection title="Tallas">
      <KvRow label="Polera"   value={detail.tshirtSize} />
      <KvRow label="Pantalón" value={detail.pantSize} />
      <KvRow label="Calzado"  value={detail.shoeSize} />
    </ModalSection>
  );

  const seccionEmergencia = detail && (
    <ModalSection title="Contacto de emergencia">
      <KvRow label="Nombre"     value={detail.emergencyContact?.name} />
      <KvRow label="Teléfono"   value={detail.emergencyContact?.phone} />
      <KvRow label="Parentesco" value={detail.emergencyContact?.relationship} />
    </ModalSection>
  );

  const seccionDocumentos = detail && (
    <ModalSection
      title="Documentos"
      aside={<span style={{ fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 500, color: "#64748b" }}>{detail.documents.length} adjunto(s)</span>}
    >
      {editing ? renderDocumentosEdicion(detail) : detail.documents.length === 0 ? (
        <div style={{ fontFamily: MODAL_FONT, fontSize: "13px", color: "#94a3b8", textAlign: "center", padding: "12px 0" }}>
          Sin documentos adjuntos
        </div>
      ) : (
        <div className="pm-doclist">
          {detail.documents.map(renderDocumentoVista)}
        </div>
      )}
    </ModalSection>
  );

  const seccionObservacion = (
    <ModalSection title="Observación">
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px 0 4px" }}>
        <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#64748b", lineHeight: 1.45 }}>
          {hasChanges
            ? "Se sugiere un resumen de los cambios. Puedes editarlo antes de validar."
            : `No se detectaron cambios: se aprobará con la observación "${OBSERVACION_SIN_CAMBIOS}" Puedes editarla.`}
        </div>
        <textarea
          value={comment}
          onChange={(e) => { setCommentTouched(true); setComment(e.target.value); }}
          rows={6}
          placeholder={`Si la dejas vacía se guardará: ${suggestion}`}
          style={{ ...editInputStyle, resize: "vertical", minHeight: "140px", lineHeight: 1.45 }}
        />
      </div>
    </ModalSection>
  );

  // Aviso "Qué revisar": con discrepancias (y sugerencias de IA) o, si la IA no
  // dejó detalle, explicando por qué el registro igual quedó en revisión.
  const avisoRevision = detail && (hayDiscrepancias || revisionSinDetalle) && (
    <div style={{ borderRadius: "12px", border: "1px solid #fde68a", background: "#fffbeb", padding: "12px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
      <div style={{ display: "flex", gap: "10px", minWidth: 0 }}>
        <span className="material-symbols-outlined" style={{ fontSize: "20px", lineHeight: 1, color: "#b45309", marginTop: "1px" }}>warning</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 600, color: "#92400e", marginBottom: "4px" }}>Qué revisar</div>
          <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#78350f", lineHeight: 1.55 }}>
            {hayDiscrepancias ? detail.detalleCedula!.map((d, i) => (
              <div key={i}><b>{etiquetaCampoCedula(d.campo)}</b>: ficha “{d.valorBase ?? "—"}” vs documento “{d.valorDocumento ?? "—"}”</div>
            )) : (
              <div>
                La validación por IA marcó este registro para revisión, pero no encontró diferencias entre la ficha y la cédula:
                la lectura del documento no fue lo bastante clara para darlo por validado. Compara la cédula con los datos y,
                si están correctos, valida el registro.
              </div>
            )}
          </div>
          {/* Resultado de "Aplicar sugerencias de IA". */}
          {resultadoIA && (
            <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px dashed #fcd34d", fontFamily: MODAL_FONT, fontSize: "12.5px", lineHeight: 1.55 }}>
              {resultadoIA.aplicadas.length > 0 ? (
                <div style={{ color: "#166534", display: "flex", gap: "6px", alignItems: "flex-start" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: "17px", lineHeight: 1.2 }}>check_circle</span>
                  <span>Sugerencias aplicadas: {resultadoIA.aplicadas.map((a) => `${a.etiqueta} (${a.anterior} → ${a.nuevo})`).join("; ")}. Revisa y guarda para confirmar.</span>
                </div>
              ) : (
                <div style={{ color: "#78350f" }}>No hubo sugerencias para aplicar: los campos ya tienen los valores leídos o no se pueden modificar.</div>
              )}
              {resultadoIA.omitidas.map((o, i) => (
                <div key={i} style={{ color: "#92400e", display: "flex", gap: "6px", alignItems: "flex-start", marginTop: "2px" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: "17px", lineHeight: 1.2 }}>info</span>
                  <span>{o.etiqueta}: no se aplicó, {o.motivo}.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {canValidate && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "6px", flexShrink: 0 }}>
          {hayDiscrepancias && (
            <button
              onClick={aplicarSugerencias}
              disabled={submitting}
              title="Inserta en los campos los valores leídos por la IA desde el documento"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "7px 14px", borderRadius: "8px", border: "none", background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)", color: "#fff", fontFamily: MODAL_FONT, fontSize: "12.5px", fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(67,56,202,0.3)" }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: "17px", lineHeight: 1 }}>auto_fix_high</span>
              Aplicar sugerencias de IA
            </button>
          )}
          {!editing && (
            <button
              onClick={startEditing}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "6px 12px", borderRadius: "8px", border: "1px solid #fcd34d", background: "#fff", color: "#92400e", fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <FactCheckIcon style={{ fontSize: 16 }} />
              {hayDiscrepancias ? "Corregir manualmente" : "Revisar y validar"}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={handleBackdropClick}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", width: "100%", maxWidth: "1280px", maxHeight: "94vh", borderRadius: "18px", overflow: "hidden", background: "#fff", boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)", display: "flex", flexDirection: "column" }}
      >
        <style>{POSTULACION_MODAL_CSS}</style>
        {/* ── header ── */}
        <div style={{ background: editing ? "linear-gradient(135deg, #0f172a 0%, #312e81 100%)" : "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", padding: "18px 28px", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
          <div style={{ position: "absolute", top: "-20px", right: "80px", width: "160px", height: "160px", background: "radial-gradient(circle, rgba(0,101,221,0.25) 0%, transparent 70%)", pointerEvents: "none" }} />
          <button
            onClick={() => pedirSalida("cerrar")}
            disabled={submitting}
            title={editing ? "Cerrar (se pregunta si hay cambios sin guardar)" : "Cerrar"}
            style={{ position: "absolute", top: "16px", right: "18px", width: "34px", height: "34px", borderRadius: "9px", border: "none", background: "rgba(255,255,255,0.1)", cursor: submitting ? "not-allowed" : "pointer", color: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.18)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)"; }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "18px", paddingRight: "48px" }}>
            <div style={{ width: "58px", height: "58px", borderRadius: "50%", background: editing ? "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)" : "linear-gradient(135deg, #3b82f6 0%, #0065DD 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MODAL_FONT, fontSize: "21px", fontWeight: 700, color: "#fff", flexShrink: 0, boxShadow: editing ? "0 4px 14px rgba(67,56,202,0.45)" : "0 4px 14px rgba(0,101,221,0.45)" }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: MODAL_FONT, fontSize: "22px", fontWeight: 700, color: "#fff", lineHeight: 1.2, letterSpacing: "0.01em" }}>
                {postulacion.names || "—"}
              </div>
              <div style={{ marginTop: "9px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Tooltip text={estadoMsg}>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 14px", borderRadius: "14px", background: estadoCfg.bg, color: estadoCfg.color, fontSize: "12.5px", fontWeight: 600, fontFamily: MODAL_FONT, border: `1px solid ${estadoCfg.color}33`, cursor: "help" }}>
                    <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: estadoCfg.dot, boxShadow: `0 0 5px ${estadoCfg.dot}`, marginRight: "7px" }} />
                    {estadoCfg.label}
                  </span>
                </Tooltip>
                {postulacion.formType && (
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 16px", borderRadius: "14px", background: tipoCfg.bg, color: tipoCfg.color, fontSize: "12.5px", fontWeight: 600, fontFamily: MODAL_FONT }}>
                    {tipoCfg.label}
                  </span>
                )}
                {/* Estado de la validación de cédula, mismo dato que la columna
                    "Validación" de la tabla. Va acá arriba para que quien abre
                    la ficha vea de una si el registro está validado, pendiente
                    o necesita intervención, sin buscar en la tabla. */}
                {(() => {
                  const cedulaCfg = resolveCedulaBadge(postulacion.veredictoCedula);
                  return (
                    <Tooltip text={cedulaBadgeTooltip(postulacion.veredictoCedula, postulacion.detalleCedula)} textTransform="none">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 14px", borderRadius: "14px", background: cedulaCfg.bg, color: cedulaCfg.color, fontSize: "12.5px", fontWeight: 600, fontFamily: MODAL_FONT, border: `1px solid ${cedulaCfg.color}33`, cursor: "help" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: "16px", lineHeight: 1 }}>{cedulaCfg.icon}</span>
                        {cedulaCfg.label}
                      </span>
                    </Tooltip>
                  );
                })()}
                {editing && (
                  <span style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "rgba(255,255,255,0.75)", marginLeft: "2px" }}>Validación manual</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── body ── */}
        <div style={{ padding: "18px 24px 22px", overflowY: "auto", flex: 1, background: "#f8fafc" }}>
          {loading ? (
            <SkeletonTheme baseColor="#eef2f6" highlightColor="#e2e8f0">
              <div className="pm-cols">
                {[0, 1].map((col) => (
                  <div key={col} style={{ borderRadius: "12px", border: "1px solid #e2e8f0", background: "#fff", padding: "14px 20px" }}>
                    <Skeleton width={160} height={16} style={{ marginBottom: 12 }} />
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", padding: "6px 0" }}>
                        <Skeleton height={12} /><Skeleton height={12} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </SkeletonTheme>
          ) : fetchError ? (
            <div style={{ textAlign: "center", padding: "32px 0", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#b91c1c" }}>{fetchError}</div>
          ) : detail ? (
            <div className="pm-body">
              {avisoRevision}

              {editing ? (
                <div className="pm-cols">
                  {seccionDatosPersonales}
                  {seccionObservacion}
                </div>
              ) : (
                <div className="pm-cols">
                  <div className="pm-stack">
                    {seccionPostulacion}
                    {seccionContacto}
                    {seccionTallas}
                  </div>
                  <div className="pm-stack">
                    {seccionDatosPersonales}
                    {seccionBancariosSalud}
                    {seccionEmergencia}
                  </div>
                </div>
              )}

              {seccionDocumentos}

              {submitError && (
                <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#b91c1c" }}>{submitError}</div>
              )}

              {/* Historial de intervenciones manuales (en vista, oculto si no hay ninguna). */}
              <HistorialValidacion personId={detail.personId} hideWhenEmpty={!editing} />
            </div>
          ) : null}
        </div>

        {/* ── footer ── (sin acciones que ofrecer —registro no validable a mano— no se
            muestra: se cierra con la X, como en el diseño de referencia) */}
        {(editing || canValidate) && (
        <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            {editing && (
              <button
                className="pm-btn-volver"
                onClick={() => pedirSalida("vista")} disabled={submitting}
                title="Volver a la vista de lectura"
                style={{ fontFamily: MODAL_FONT }}
              >
                <span className="material-symbols-outlined pm-btn-volver__icono">arrow_back</span>
                Volver
              </button>
            )}
            <span style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#94a3b8" }}>
              {editing
                ? hasChanges ? `${fieldChanges.length} dato(s) y ${docChanges.length + rotChanges.length} documento(s) por cambiar` : "Sin cambios"
                : ""}
            </span>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            {editing ? (
              <>
                <button
                  onClick={handleSubmit} disabled={!canSubmit}
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 20px", borderRadius: "9px", border: "none", background: canSubmit ? "#4338ca" : "#c7d2fe", color: "#fff", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed" }}
                >
                  {submitting && <CircularProgress size={14} thickness={5} sx={{ color: "#fff" }} />}
                  {hasChanges ? "Guardar y validar" : "Aprobar sin cambios"}
                </button>
              </>
            ) : (
              <>
                <motion.button
                  onClick={onClose}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{ padding: "8px 22px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                >
                  Cerrar
                </motion.button>
                {canValidate && (
                  <motion.button
                    onClick={startEditing}
                    disabled={!detail}
                    whileHover={detail ? { scale: 1.03 } : {}}
                    whileTap={detail ? { scale: 0.97 } : {}}
                    style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 20px", borderRadius: "9px", border: "none", background: detail ? "#4338ca" : "#c7d2fe", color: "#fff", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 600, cursor: detail ? "pointer" : "not-allowed" }}
                  >
                    <FactCheckIcon style={{ fontSize: 17 }} />
                    Validar datos
                  </motion.button>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {/* ── confirmación: salir de la edición con cambios sin guardar ── */}
        <AnimatePresence>
          {salidaPendiente && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
              onClick={() => setSalidaPendiente(null)}
              style={{ position: "absolute", inset: 0, zIndex: 30, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
            >
              <motion.div
                role="alertdialog" aria-modal="true" aria-labelledby="pm-salida-titulo"
                initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 320, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: "500px", borderRadius: "14px", background: "#fff", boxShadow: "0 20px 48px rgba(15,23,42,0.3)", overflow: "hidden" }}
              >
                <div style={{ padding: "20px 22px 4px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <span style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#fffbeb", color: "#b45309", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: "22px", lineHeight: 1 }}>warning</span>
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div id="pm-salida-titulo" style={{ fontFamily: MODAL_FONT, fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>Tienes cambios sin guardar</div>
                    <div style={{ marginTop: "4px", fontFamily: MODAL_FONT, fontSize: "13px", color: "#475569", lineHeight: 1.5 }}>
                      {salidaPendiente === "vista"
                        ? "Si vuelves a la vista de lectura sin guardar, se descartan estos cambios:"
                        : "Si cierras sin guardar, se descartan estos cambios:"}
                    </div>
                    <ul style={{ margin: "8px 0 0", paddingLeft: "18px", fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#334155", lineHeight: 1.6, maxHeight: "160px", overflowY: "auto" }}>
                      {fieldChanges.map((c) => <li key={c.campo}><b>{c.campo}</b>: {c.valorAnterior ?? "—"} → {c.valorNuevo ?? "—"}</li>)}
                      {docChanges.map((d) => <li key={`doc-${d.type}`}><b>{d.label}</b>: documento reemplazado</li>)}
                      {rotChanges.map((r) => <li key={`rot-${r.type}`}><b>{r.label}</b>: rotación de {r.giro}°</li>)}
                      {commentTouched && comment.trim() !== "" && <li><b>Observación</b> escrita</li>}
                    </ul>
                  </div>
                </div>
                <div style={{ padding: "16px 22px 18px", display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    onClick={() => setSalidaPendiente(null)} autoFocus
                    style={{ padding: "8px 16px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                  >
                    Seguir editando
                  </button>
                  <button
                    onClick={descartarYSalir}
                    style={{ padding: "8px 16px", borderRadius: "9px", border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                  >
                    Descartar cambios
                  </button>
                  <button
                    onClick={guardarDesdeDialogo} disabled={!canSubmit}
                    style={{ padding: "8px 16px", borderRadius: "9px", border: "none", background: canSubmit ? "#4338ca" : "#c7d2fe", color: "#fff", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed" }}
                  >
                    {hasChanges ? "Guardar y validar" : "Aprobar sin cambios"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// ─── component ────────────────────────────────────────────────────────────────

interface TablaPostulacionesProps {
  activeTab: "postulaciones" | "formularios";
  // Cuando es true, muestra el botón oculto "Sincronizar registros" (se activa
  // tras 20 clics en el logo, controlado por el componente padre).
  syncUnlocked?: boolean;
  // Pestaña Prefiltrado: limita la tabla (y el export) a un grupo de un
  // prefiltro. Los filtros de la tabla se siguen aplicando encima.
  prefilter?: IPrefilterSelection | null;
}

const TablaPostulaciones = ({ activeTab, syncUnlocked = false, prefilter = null }: TablaPostulacionesProps) => {
  // El fetch depende de la clave (texto), no del objeto: así un objeto nuevo con
  // los mismos valores no cancela una petición en curso.
  const prefilterKey = prefilter ? `${prefilter.prefilterId}:${prefilter.bucket}` : "";
  const prefilterRef = useRef(prefilter);
  prefilterRef.current = prefilter;
  const history  = useHistory();
  const location = useLocation();

  // Perfil y usuario en sesión: el reclutador (tw-sel) tiene la acción de
  // asignar restringida (ver isAssignable). Se leen una sola vez.
  const profile      = useMemo(() => sessionStorage.getItem("profile") ?? "", []);
  const loggedUserId = useMemo(() => sessionStorage.getItem("usuarioId") ?? "", []);
  const isRecruiterProfile = profile === FILTER_PROFILE.tw_sel;
  const isTwMarketing = isTwMarketingProfile(profile);
  // Migración manual de datos personales al legacy: solo para TI.
  const canMigratePersonalData = profile === FILTER_PROFILE.adm_ti;
  // Qué datos cuentan para la completitud, por cliente / área / empresa.
  const canConfigureCompleteness = profile === FILTER_PROFILE.adm_tw || profile === FILTER_PROFILE.adm_ti;
  const canSelectApplications = true;
  const showApplicationActions = true;
  const availableColumnDefinitions = TABLE_COLUMN_DEFINITIONS;
  const primaryFilterColumns = PRIMARY_FILTER_COLUMNS;
  const advancedFilterColumns = ADVANCED_FILTER_COLUMNS;
  const columnsStorageKey = useMemo(
    () => `teamwork:postulaciones:visible-columns:${loggedUserId || "anonymous"}`,
    [loggedUserId],
  );
  const marketingFullAccessMigrationKey = useMemo(
    () => `teamwork:postulaciones:tw-marketing-full-access:${TW_MARKETING_FULL_ACCESS_MIGRATION_VERSION}:${loggedUserId || "anonymous"}`,
    [loggedUserId],
  );
  const marketingFullAccessMigrationPendingRef = useRef(
    isTwMarketing && needsBusinessAreaPreferenceMigration(marketingFullAccessMigrationKey),
  );
  const personalColumnsMigrationKey = useMemo(
    () => `teamwork:postulaciones:personal-columns:${PERSONAL_COLUMNS_MIGRATION_VERSION}:${loggedUserId || "anonymous"}`,
    [loggedUserId],
  );
  const personalColumnsMigrationPendingRef = useRef(
    needsBusinessAreaPreferenceMigration(personalColumnsMigrationKey),
  );
  const applyPendingColumnMigrations = useCallback(
    (keys: TableColumnKey[]): TableColumnKey[] => {
      let result = keys;
      if (marketingFullAccessMigrationPendingRef.current) {
        result = availableColumnDefinitions.map(({ key }) => key);
      }
      if (personalColumnsMigrationPendingRef.current) {
        result = withColumnsVisible(result, availableColumnDefinitions, PERSONAL_COLUMNS_TO_REVEAL);
      }
      return result;
    },
    [availableColumnDefinitions],
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<TableColumnKey[]>(
    () => applyPendingColumnMigrations(
      readVisibleColumns(columnsStorageKey, availableColumnDefinitions),
    ),
  );
  // Orden de TODAS las columnas (visibles u ocultas) elegido por el usuario. Se
  // guarda junto con la visibilidad en su preferencia (columnOrder).
  const [columnOrder, setColumnOrder] = useState<TableColumnKey[]>(
    () => readColumnOrder(columnOrderStorageKey(columnsStorageKey), DEFAULT_COLUMN_ORDER),
  );
  const orderedColumnDefinitions = useMemo(
    () => columnOrder
      .map((key) => availableColumnDefinitions.find((column) => column.key === key))
      .filter((column): column is TableColumnDefinition => Boolean(column)),
    [availableColumnDefinitions, columnOrder],
  );
  const [columnPreferenceLoaded, setColumnPreferenceLoaded] = useState(false);
  const [isSavingColumnPreference, setIsSavingColumnPreference] = useState(false);
  const [isColumnPreferenceSynchronized, setIsColumnPreferenceSynchronized] = useState<boolean | null>(null);

  const { filters: urlFilters, search: urlSearch, page: urlPage, limit: urlLimit } = useMemo(
    () => parseUrl(location.search),
    [location.search],
  );

  const [filterInputs, setFilterInputs] = useState<FilterInputs>(EMPTY_INPUTS);
  const [showFilters, setShowFilters]   = useState<boolean>(false);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const [selected, setSelected]         = useState<IApplicationApi | null>(null);
  const [catalogFilterOptions, setCatalogFilterOptions] = useState<Record<"shift" | "position" | "locality" | "formCategory" | "gender", FilterOption[]>>({
    shift: [],
    position: [],
    locality: [],
    formCategory: [],
    gender: [],
  });

  const [applications, setApplications] = useState<IApplicationApi[]>([]);
  const [pagination, setPagination]     = useState<IPaginationApi | null>(null);
  const [firstLoad, setFirstLoad]       = useState<boolean>(true);
  const [isFetching, setIsFetching]     = useState<boolean>(false);
  const [refreshKey, setRefreshKey]     = useState<number>(0);
  const [isRefreshingApplications, setIsRefreshingApplications] = useState<boolean>(false);
  const [exporting, setExporting]       = useState<boolean>(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [syncing, setSyncing]           = useState<boolean>(false);
  const [syncMsg, setSyncMsg]           = useState<string | null>(null);
  const [migratingPersonalData, setMigratingPersonalData] = useState<boolean>(false);
  const [lastPersonalDataRun, setLastPersonalDataRun] = useState<IMigracionDatosCorrida | null>(null);
  // Corrida en curso en el servidor (timer, otro usuario u otra pestaña): solo
  // puede haber una a la vez, así que el botón queda bloqueado mientras dure.
  const [personalDataRunning, setPersonalDataRunning] = useState<boolean>(false);
  const [calculatingCompleteness, setCalculatingCompleteness] = useState<boolean>(false);
  const [showCompletenessConfig, setShowCompletenessConfig] = useState<boolean>(false);
  const [lastCompletenessRun, setLastCompletenessRun] = useState<ICalculoCompletitudCorrida | null>(null);
  const [completenessRunning, setCompletenessRunning] = useState<boolean>(false);
  const [downloadingDocsFor, setDownloadingDocsFor] = useState<string | null>(null);
  // applicationId con una acción de "WhatsApp 2ª parte" en curso (enviar ahora / extender).
  const [invitationActionFor, setInvitationActionFor] = useState<string | null>(null);
  const [downloadingFichaFor, setDownloadingFichaFor] = useState<string | null>(null);
  const [downloadingSelectedFichas, setDownloadingSelectedFichas] = useState<boolean>(false);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [assignModalItems, setAssignModalItems] = useState<IApplicationApi[] | null>(null);
  const [hasHorizontalTableScroll, setHasHorizontalTableScroll] = useState(false);
  const [canScrollTableLeft, setCanScrollTableLeft] = useState(false);
  const [canScrollTableRight, setCanScrollTableRight] = useState(false);
  const [isDraggingTable, setIsDraggingTable] = useState(false);
  const [tableScrollTop, setTableScrollTop] = useState(0);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollAnimationRef = useRef<number | null>(null);
  const tableDragRef = useRef({ active: false, startX: 0, initialScrollLeft: 0 });
  const backgroundRefreshRef = useRef(false);
  const firstLoadRef                    = useRef<boolean>(true);
  const lastFetchKeyRef                 = useRef<string | null>(null);
  const prevTabRef                      = useRef<string>(activeTab);
  const locationSearchRef               = useRef(location.search);
  locationSearchRef.current             = location.search;
  const [error, setError]               = useState<string | null>(null);

  const visibleTableColumns = useMemo(
    () => orderedColumnDefinitions.filter(({ key }) => visibleColumnKeys.includes(key)),
    [orderedColumnDefinitions, visibleColumnKeys],
  );
  const tableMinWidth = useMemo(
    () => Math.max(
      700,
      (canSelectApplications ? 38 : 0)
        + (showApplicationActions ? 172 : 0)
        + visibleTableColumns.reduce((total, column) => total + column.width, 0),
    ),
    [canSelectApplications, showApplicationActions, visibleTableColumns],
  );
  const renderedTableColumnCount = visibleTableColumns.length
    + (canSelectApplications ? 1 : 0)
    + (showApplicationActions ? 1 : 0);
  const shouldVirtualizeTable = applications.length > TABLE_VIRTUALIZATION_THRESHOLD;
  const virtualStartIndex = shouldVirtualizeTable
    ? Math.max(0, Math.floor(tableScrollTop / TABLE_VIRTUAL_ROW_HEIGHT) - TABLE_VIRTUAL_OVERSCAN)
    : 0;
  const virtualEndIndex = shouldVirtualizeTable
    ? Math.min(
        applications.length,
        Math.ceil((tableScrollTop + TABLE_VIRTUAL_VIEWPORT_HEIGHT) / TABLE_VIRTUAL_ROW_HEIGHT) + TABLE_VIRTUAL_OVERSCAN,
      )
    : applications.length;
  const renderedApplications = shouldVirtualizeTable
    ? applications.slice(virtualStartIndex, virtualEndIndex)
    : applications;
  const virtualTopSpacerHeight = virtualStartIndex * TABLE_VIRTUAL_ROW_HEIGHT;
  const virtualBottomSpacerHeight = Math.max(
    0,
    (applications.length - virtualEndIndex) * TABLE_VIRTUAL_ROW_HEIGHT,
  );

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    let cancelled = false;

    if (!baseUrl || !loggedUserId) {
      setColumnPreferenceLoaded(true);
      return;
    }

    const params = new URLSearchParams({
      userId: loggedUserId,
      table: APPLICATIONS_TABLE_PREFERENCE_KEY,
    });

    fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setIsColumnPreferenceSynchronized(true);
        const normalizedRemoteColumns = normalizeVisibleColumns(
          data?.preference?.visibleColumns,
          availableColumnDefinitions,
        );
        const remoteColumns = normalizedRemoteColumns
          ? applyPendingColumnMigrations(normalizedRemoteColumns)
          : normalizedRemoteColumns;
        if (remoteColumns) {
          setVisibleColumnKeys(remoteColumns);
          try {
            localStorage.setItem(columnsStorageKey, JSON.stringify(remoteColumns));
          } catch {
            // La preferencia remota sigue disponible aunque localStorage esté bloqueado.
          }
        }
        // Sin columnOrder (preferencia guardada antes del drag and drop) se
        // mantiene el orden local.
        if (Array.isArray(data?.preference?.columnOrder)) {
          const remoteOrder = mergeColumnOrder(data.preference.columnOrder, DEFAULT_COLUMN_ORDER);
          setColumnOrder(remoteOrder);
          try {
            localStorage.setItem(columnOrderStorageKey(columnsStorageKey), JSON.stringify(remoteOrder));
          } catch {
            // Igual que arriba: el orden remoto se aplica aunque no se pueda copiar localmente.
          }
        }
      })
      .catch(() => {
        // Mantiene la copia local como respaldo mientras el servicio no esté disponible.
        if (!cancelled) setIsColumnPreferenceSynchronized(false);
      })
      .finally(() => {
        if (!cancelled) setColumnPreferenceLoaded(true);
      });

    return () => { cancelled = true; };
  }, [availableColumnDefinitions, columnsStorageKey, loggedUserId]);

  useEffect(() => {
    try {
      localStorage.setItem(columnsStorageKey, JSON.stringify(visibleColumnKeys));
      localStorage.setItem(columnOrderStorageKey(columnsStorageKey), JSON.stringify(columnOrder));
    } catch {
      // La tabla sigue funcionando aunque el navegador bloquee localStorage.
    }
    if (!columnPreferenceLoaded || !loggedUserId) return;

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;

    let controller: AbortController | null = null;
    const saveTimer = window.setTimeout(() => {
      controller = new AbortController();
      setIsSavingColumnPreference(true);
      const params = new URLSearchParams({
        userId: loggedUserId,
        table: APPLICATIONS_TABLE_PREFERENCE_KEY,
      });
      fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // visibleColumns va en el orden del usuario; columnOrder incluye las ocultas.
        body: JSON.stringify({
          visibleColumns: columnOrder.filter((key) => visibleColumnKeys.includes(key)),
          columnOrder,
        }),
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          setIsColumnPreferenceSynchronized(true);
          if (marketingFullAccessMigrationPendingRef.current) {
            try {
              localStorage.setItem(marketingFullAccessMigrationKey, "complete");
              marketingFullAccessMigrationPendingRef.current = false;
            } catch {
              // La columna seguirá visible aunque el navegador bloquee localStorage.
            }
          }
          if (personalColumnsMigrationPendingRef.current) {
            try {
              localStorage.setItem(personalColumnsMigrationKey, "complete");
              personalColumnsMigrationPendingRef.current = false;
            } catch {
              // Las columnas seguirán visibles aunque el navegador bloquee localStorage.
            }
          }
        })
        .catch((requestError) => {
          if (requestError?.name !== "AbortError") {
            // La copia local conserva el cambio y se volverá a intentar en la próxima modificación.
            setIsColumnPreferenceSynchronized(false);
          }
        })
        .finally(() => {
          if (!controller?.signal.aborted) setIsSavingColumnPreference(false);
        });
    }, 350);

    return () => {
      window.clearTimeout(saveTimer);
      controller?.abort();
    };
  }, [columnOrder, columnPreferenceLoaded, columnsStorageKey, loggedUserId, marketingFullAccessMigrationKey, personalColumnsMigrationKey, visibleColumnKeys]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;

    let cancelled = false;
    const fetchOptions = async (
      endpoint: string,
      responseKey: string,
    ): Promise<FilterOption[]> => {
      const response = await fetch(`${baseUrl}/catalogs/${endpoint}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const rows = Array.isArray(data?.[responseKey]) ? data[responseKey] : [];
      const names = rows
        .map((row: any) => typeof row?.name === "string" ? row.name.trim() : "")
        .filter(Boolean);
      return Array.from(new Set<string>(names)).map((name) => ({ value: name, label: name }));
    };

    Promise.all([
      fetchOptions("shifts", "shifts").catch(() => []),
      fetchOptions("positions", "positions").catch(() => []),
      fetchOptions("localities", "localities").catch(() => []),
      fetchOptions("form-categories", "formCategories").catch(() => []),
      // Los géneros ya viajan en el catálogo de identificación; no hace falta
      // un endpoint nuevo solo para poblar este filtro.
      fetchOptions("identification", "genders").catch(() => []),
    ]).then(([shift, position, locality, formCategory, gender]) => {
      if (!cancelled) setCatalogFilterOptions({ shift, position, locality, formCategory, gender });
    });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (urlFilters.business.length > 0 || urlFilters.vacancy.length > 0) {
      setShowMoreFilters(true);
    }
  }, [urlFilters.business.length, urlFilters.vacancy.length]);

  const selectOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const mergeCurrentValues = (key: "shift" | "position" | "locality") => {
      const result = [...catalogFilterOptions[key]];
      const known = new Set(result.map((option) => option.value));
      applications.forEach((application) => {
        const value = application[key]?.trim();
        if (value && !known.has(value)) {
          known.add(value);
          result.push({ value, label: value });
        }
      });
      return result;
    };

    return {
      ...STATIC_SELECT_OPTIONS,
      shift: mergeCurrentValues("shift"),
      position: mergeCurrentValues("position"),
      locality: mergeCurrentValues("locality"),
      formCategory: catalogFilterOptions.formCategory,
      gender: catalogFilterOptions.gender,
    };
  }, [applications, catalogFilterOptions]);

  // ── url updater ──────────────────────────────────────────────────────────────
  const updateUrl = useCallback(
    (filters: ActiveFilters, page: number, limit: number) => {
      const newSearch = buildSearch(filters, page, limit, locationSearchRef.current);
      history.replace({ pathname: location.pathname, search: `?${newSearch}` });
    },
    [history, location.pathname],
  );

  // Buscador global: vive en la URL (como los filtros) y siempre vuelve a la página 1.
  const updateSearch = useCallback(
    (term: string) => {
      const sp = new URLSearchParams(locationSearchRef.current);
      if (term) sp.set(SEARCH_URL_PARAM, term);
      else sp.delete(SEARCH_URL_PARAM);
      sp.set(`${URL_PREFIX}page`, "1");
      history.replace({ pathname: location.pathname, search: `?${sp.toString()}` });
    },
    [history, location.pathname],
  );

  // Bust cache when switching back to postulaciones so the table always reloads
  useEffect(() => {
    if (activeTab === "postulaciones" && prevTabRef.current === "formularios") {
      lastFetchKeyRef.current = null;
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);

  const updateTableScrollState = useCallback(() => {
    const element = tableScrollRef.current;
    if (!element) return;
    const hasOverflow = element.scrollWidth > element.clientWidth + 1;
    setHasHorizontalTableScroll(hasOverflow);
    setCanScrollTableLeft(hasOverflow && element.scrollLeft > 2);
    setCanScrollTableRight(
      hasOverflow && element.scrollLeft + element.clientWidth < element.scrollWidth - 2,
    );
  }, []);

  const handleTableScroll = useCallback(() => {
    if (tableScrollAnimationRef.current !== null) return;
    tableScrollAnimationRef.current = window.requestAnimationFrame(() => {
      tableScrollAnimationRef.current = null;
      const element = tableScrollRef.current;
      if (!element) return;
      setTableScrollTop(element.scrollTop);
      updateTableScrollState();
    });
  }, [updateTableScrollState]);

  useEffect(() => () => {
    if (tableScrollAnimationRef.current !== null) {
      window.cancelAnimationFrame(tableScrollAnimationRef.current);
    }
  }, []);

  useEffect(() => {
    setTableScrollTop(0);
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [location.search]);

  useEffect(() => {
    const element = tableScrollRef.current;
    if (!element || activeTab !== "postulaciones") return;

    updateTableScrollState();
    const observer = new ResizeObserver(updateTableScrollState);
    observer.observe(element);
    window.addEventListener("resize", updateTableScrollState);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateTableScrollState);
    };
  }, [activeTab, updateTableScrollState, visibleColumnKeys]);

  const handleTablePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !hasHorizontalTableScroll) return;
    const target = event.target as HTMLElement;
    if (target.closest("button, input, a, textarea, select, [role='button']")) return;

    const element = tableScrollRef.current;
    if (!element) return;
    tableDragRef.current = {
      active: true,
      startX: event.clientX,
      initialScrollLeft: element.scrollLeft,
    };
    setIsDraggingTable(true);
    element.setPointerCapture(event.pointerId);
  };

  const handleTablePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!tableDragRef.current.active) return;
    const element = tableScrollRef.current;
    if (!element) return;
    event.preventDefault();
    element.scrollLeft = tableDragRef.current.initialScrollLeft - (event.clientX - tableDragRef.current.startX);
    updateTableScrollState();
  };

  const finishTableDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!tableDragRef.current.active) return;
    tableDragRef.current.active = false;
    setIsDraggingTable(false);
    const element = tableScrollRef.current;
    if (element?.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
    updateTableScrollState();
  };

  // ── fetch when URL or tab changes ────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "postulaciones") return;

    const fetchKey = FILTER_KEYS.map((k) => `${k}:${urlFilters[k].join(",")}`).join("|") + `|q${urlSearch}|p${urlPage}|l${urlLimit}|pf${prefilterKey}`;
    if (lastFetchKeyRef.current === fetchKey && !firstLoadRef.current) return;
    lastFetchKeyRef.current = fetchKey;

    const userId  = sessionStorage.getItem("usuarioId") ?? "";
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setError("Configuración faltante: REACT_APP_API_APPLICANT");
      setApplications([]);
      setPagination(null);
      firstLoadRef.current = false;
      setFirstLoad(false);
      backgroundRefreshRef.current = false;
      setIsRefreshingApplications(false);
      return;
    }
    if (!userId) {
      setError("No se pudo identificar el usuario actual.");
      setApplications([]);
      setPagination(null);
      firstLoadRef.current = false;
      setFirstLoad(false);
      backgroundRefreshRef.current = false;
      setIsRefreshingApplications(false);
      return;
    }

    let cancelled = false;
    // Si cambian los parámetros antes de que responda (p. ej. la persona sigue
    // escribiendo en el buscador), se cancela la petición anterior.
    const controller = new AbortController();
    const isInitialThisRun = firstLoadRef.current;

    setIsFetching(true);

    const filtersBody = FILTER_KEYS.reduce<Record<string, string[] | string>>((acc, k) => {
      if (urlFilters[k].length > 0) acc[k] = urlFilters[k];
      return acc;
    }, {});
    if (urlSearch) filtersBody.search = urlSearch;

    const url =
      `${baseUrl}/application/list` +
      `?page=${urlPage}` +
      `&limit=${urlLimit}` +
      `&userId=${encodeURIComponent(userId)}`;

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filters: filtersBody,
        ...(prefilterRef.current ? { prefilter: prefilterRef.current } : {}),
      }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readApiErrorMessage(res));
        return (await res.json()) as IApplicationListResponse;
      })
      .then((data) => {
        if (cancelled) return;
        setApplications(Array.isArray(data?.applications) ? data.applications : []);
        setPagination(data?.pagination ?? null);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (isInitialThisRun) {
          setApplications([]);
          setPagination(null);
        }
        setError(getUserErrorMessage(err, "No se pudo cargar el listado de postulaciones."));
      })
      .finally(() => {
        if (cancelled) return;
        setIsFetching(false);
        if (backgroundRefreshRef.current) {
          backgroundRefreshRef.current = false;
          setIsRefreshingApplications(false);
        }
        if (isInitialThisRun) {
          firstLoadRef.current = false;
          setFirstLoad(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [urlFilters, urlSearch, urlPage, urlLimit, activeTab, refreshKey, prefilterKey]);

  // ── filter actions ───────────────────────────────────────────────────────────

  const rutCandidates = useMemo(
    () => parseRutList(filterInputs.DNI),
    [filterInputs.DNI],
  );

  const applyRutFilters = () => {
    if (rutCandidates.length === 0) return;

    const validRuts = rutCandidates
      .filter((candidate) => candidate.isValid)
      .map((candidate) => candidate.formatted);
    const invalidEntries = rutCandidates
      .filter((candidate) => !candidate.isValid)
      .map((candidate) => candidate.raw.trim())
      .filter(Boolean);

    setFilterInputs((prev) => ({ ...prev, DNI: invalidEntries.join("\n") }));
    if (validRuts.length === 0) return;

    const nextDniFilters = Array.from(new Set([...urlFilters.DNI, ...validRuts]));
    updateUrl({ ...urlFilters, DNI: nextDniFilters }, 1, urlLimit);
  };

  const addFilter = (key: FilterKey, value: string) => {
    const v = value.trim();
    if (!v) return;
    if (urlFilters[key].includes(v)) {
      setFilterInputs((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    const next: ActiveFilters = { ...urlFilters, [key]: [...urlFilters[key], v] };
    setFilterInputs((prev) => ({ ...prev, [key]: "" }));
    updateUrl(next, 1, urlLimit);
  };

  const removeFilter = (key: FilterKey, value: string) => {
    const next: ActiveFilters = { ...urlFilters, [key]: urlFilters[key].filter((v) => v !== value) };
    updateUrl(next, 1, urlLimit);
  };

  const toggleSelectFilter = (key: FilterKey, value: string) => {
    if (urlFilters[key].includes(value)) {
      removeFilter(key, value);
    } else {
      addFilter(key, value);
    }
  };

  const clearAllFilters = () => {
    if (filtersEqual(urlFilters, EMPTY_FILTERS)) return;
    setFilterInputs(EMPTY_INPUTS);
    updateUrl(EMPTY_FILTERS, 1, urlLimit);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, key: FilterKey) => {
    if (e.key === "Enter") addFilter(key, filterInputs[key]);
  };

  const handlePageChange     = (p: number) => updateUrl(urlFilters, p, urlLimit);
  const handlePageSizeChange = (s: number) => updateUrl(urlFilters, 1, s);

  const totalActiveFilters = useMemo(
    () => FILTER_KEYS.reduce((acc, k) => acc + urlFilters[k].length, 0),
    [urlFilters],
  );

  const handleRefreshApplications = () => {
    if (isRefreshingApplications || isFetching) return;
    backgroundRefreshRef.current = true;
    setIsRefreshingApplications(true);
    lastFetchKeyRef.current = null;
    setRefreshKey((current) => current + 1);
  };

  // Acciones de la columna "WhatsApp 2ª parte". El backend registra el usuario
  // de la sesión; al terminar se recarga la tabla para ver el estado nuevo.
  const runInvitationAction = async (
    item: IApplicationApi,
    action: (applicationId: string) => Promise<{ message: string }>,
    fallbackError: string,
  ) => {
    if (invitationActionFor) return;
    setInvitationActionFor(item.applicationId);
    setSyncMsg(null);
    setError(null);
    try {
      const result = await action(item.applicationId);
      setSyncMsg(`${item.code}: ${result.message}`);
    } catch (err) {
      setError(`${item.code}: ${getUserErrorMessage(err, fallbackError)}`);
    } finally {
      setInvitationActionFor(null);
      handleRefreshApplications();
    }
  };

  const handleSendInvitationNow = (item: IApplicationApi) => {
    if (!window.confirm(`¿Enviar ahora el WhatsApp con la segunda parte del formulario a ${item.names ?? item.code}? No se esperará la hora programada.`)) return;
    void runInvitationAction(item, enviarInvitacionAhora, "No se pudo enviar el WhatsApp.");
  };

  const handleExtendInvitation = (item: IApplicationApi) => {
    if (!window.confirm(`¿Reactivar el enlace de ${item.names ?? item.code}? Podrá volver a entrar con el mismo enlace que ya recibió, sin vencimiento, hasta que complete la segunda parte.`)) return;
    void runInvitationAction(item, (applicationId) => extenderInvitacion(applicationId), "No se pudo reactivar el enlace.");
  };

  const triggerBlobDownload = (blob: Blob, fallbackName: string, contentDisposition: string | null) => {
    let filename = fallbackName;
    if (contentDisposition) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^;"'\r\n]+)["']?/i.exec(contentDisposition);
      if (match?.[1]) filename = decodeURIComponent(match[1]);
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    setExportNotice(null);
    setError(null);
    // Aviso que queda visible al terminar (descarga recortada a los primeros 5000).
    let finalNotice: string | null = null;
    try {
      const userId  = sessionStorage.getItem("usuarioId") ?? "";
      const baseUrl = process.env.REACT_APP_API_APPLICANT;
      if (!baseUrl || !userId) throw new Error("Configuración faltante o usuario no identificado.");

      // Con selección: exporta solo esos registros (incluye "En proceso activo"),
      // y una selección de más de 5000 se rechaza.
      // Sin selección: exporta el listado filtrado, hasta los primeros 5000.
      const selectedApplicationIds = applications
        .filter((a) => selectedCodes.has(a.code) && a.applicationId)
        .map((a) => a.applicationId as string);
      const hasSelection = selectedApplicationIds.length > 0;

      if (hasSelection && excedeLimiteDescarga(selectedApplicationIds.length)) {
        setError(mensajeLimiteDescarga(selectedApplicationIds.length));
        return;
      }

      // Con prefiltro (sin selección) el export trae el grupo completo, no el
      // total de la tabla: ahí solo el backend sabe si se recorta.
      const tableTotal = pagination?.totalRecords ?? applications.length;
      const willBeTruncated = !hasSelection && (prefilter ? true : excedeLimiteDescarga(tableTotal));
      const totalExportRecords = hasSelection
        ? selectedApplicationIds.length
        : Math.min(tableTotal, MAX_REGISTROS_DESCARGA);
      const isUnfilteredFullExport = totalActiveFilters === 0 && !urlSearch && !prefilter && !hasSelection;
      if (willBeTruncated) {
        setExportNotice(`${AVISO_DESCARGA_SIN_SELECCION} Por el volumen, la descarga podría tardar varios segundos.`);
      } else if (isUnfilteredFullExport || totalExportRecords > 500) {
        setExportNotice(
          totalExportRecords > 500
            ? `Estamos generando un archivo con ${totalExportRecords.toLocaleString("es-CL")} registros. Por el volumen, la descarga podría tardar varios segundos.`
            : "Estamos generando el archivo sin filtros aplicados. La descarga podría tardar varios segundos.",
        );
      }

      const body = selectedApplicationIds.length > 0
        ? { applicationIds: selectedApplicationIds }
        : {
            filters: {
              ...FILTER_KEYS.reduce<Record<string, string[]>>((acc, k) => {
                if (urlFilters[k].length > 0) acc[k] = urlFilters[k];
                return acc;
              }, {}),
              ...(urlSearch ? { search: urlSearch } : {}),
            },
            // Con prefiltro el export trae el grupo completo (sin los filtros de la tabla).
            ...(prefilter ? { prefilter } : {}),
          };

      const res = await fetch(
        `${baseUrl}/application/export?userId=${encodeURIComponent(userId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) throw new Error(await readApiErrorMessage(res));
      const blob = await res.blob();
      triggerBlobDownload(
        blob,
        `nomina_seleccion_${new Date().toISOString().slice(0, 10)}.xlsx`,
        res.headers.get("Content-Disposition"),
      );
      const recorte = leerRecorteDescarga(res.headers);
      if (recorte.recortado) {
        finalNotice = mensajeDescargaRecortada(recorte.total ?? (prefilter ? undefined : tableTotal));
      }
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudo generar el Excel."));
    } finally {
      setExporting(false);
      setExportNotice(finalNotice);
    }
  };

  // Última corrida de la migración de datos personales (timer o manual), para
  // el tooltip del botón, y si hay una en curso ahora mismo. Si falla la
  // consulta, el botón funciona igual (el backend rechaza una segunda corrida).
  const loadLastPersonalDataRun = useCallback(async () => {
    try {
      const { runs, running } = await getEstadoMigracionDatos();
      setLastPersonalDataRun(runs[0] ?? null);
      setPersonalDataRunning(running);
    } catch {
      setLastPersonalDataRun(null);
      setPersonalDataRunning(false);
    }
  }, []);

  useEffect(() => {
    if (!canMigratePersonalData) return;
    void loadLastPersonalDataRun();
    // El timer corre solo cada 10 min: se consulta seguido para que el botón
    // refleje una corrida lanzada por el timer o por otro usuario.
    const interval = window.setInterval(() => void loadLastPersonalDataRun(), JOB_STATUS_POLL_MS);
    return () => window.clearInterval(interval);
  }, [canMigratePersonalData, loadLastPersonalDataRun]);

  const personalDataBusy = migratingPersonalData || personalDataRunning;

  const handleMigratePersonalData = async () => {
    if (personalDataBusy) return;
    setMigratingPersonalData(true);
    setSyncMsg(null);
    setError(null);
    try {
      const result = await migrarDatosPostulantes();
      setSyncMsg(result.message);
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudieron migrar los datos de los postulantes."));
    } finally {
      setMigratingPersonalData(false);
      void loadLastPersonalDataRun();
    }
  };

  const migratePersonalDataTooltip = useMemo(() => {
    const base = personalDataRunning && !migratingPersonalData
      ? "Ya hay una migración en ejecución (automática o de otro usuario). Solo puede correr una a la vez: el botón se habilita cuando termine."
      : "Migra al sistema de reclutamiento los datos personales de quienes se registraron o actualizaron desde la última sincronización. Se ejecuta sola cada 10 minutos.";
    if (!lastPersonalDataRun) return base;
    const [date = "", time = ""] = lastPersonalDataRun.startedAt.split("T");
    const [, month = "", day = ""] = date.split("-");
    const origin = lastPersonalDataRun.origin === "MANUAL" ? "manual" : "automática";
    return `${base} Última (${origin}): ${day}-${month} ${time.slice(0, 5)} · ${lastPersonalDataRun.status.toLowerCase()} · ${lastPersonalDataRun.migrated} de ${lastPersonalDataRun.total} migradas.`;
  }, [lastPersonalDataRun, personalDataRunning, migratingPersonalData]);

  // Completitud: la calcula un proceso en segundo plano cada 5 minutos (la
  // tabla solo la lee). Mismo esquema que la migración de datos: el botón
  // adelanta la corrida y el tooltip muestra la última.
  const loadLastCompletenessRun = useCallback(async () => {
    try {
      const { runs, running } = await getEstadoCalculoCompletitud();
      setLastCompletenessRun(runs[0] ?? null);
      setCompletenessRunning(running);
    } catch {
      setLastCompletenessRun(null);
      setCompletenessRunning(false);
    }
  }, []);

  useEffect(() => {
    if (!canMigratePersonalData) return;
    void loadLastCompletenessRun();
    const interval = window.setInterval(() => void loadLastCompletenessRun(), JOB_STATUS_POLL_MS);
    return () => window.clearInterval(interval);
  }, [canMigratePersonalData, loadLastCompletenessRun]);

  const completenessBusy = calculatingCompleteness || completenessRunning;

  const handleCalculateCompleteness = async () => {
    if (completenessBusy) return;
    setCalculatingCompleteness(true);
    setSyncMsg(null);
    setError(null);
    try {
      const result = await calcularCompletitud();
      setSyncMsg(result.message);
      handleRefreshApplications();
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudo calcular la completitud de las postulaciones."));
    } finally {
      setCalculatingCompleteness(false);
      void loadLastCompletenessRun();
    }
  };

  const completenessTooltip = useMemo(() => {
    const base = completenessRunning && !calculatingCompleteness
      ? "Ya hay un cálculo de completitud en ejecución (automático o de otro usuario). Solo puede correr uno a la vez: el botón se habilita cuando termine."
      : "Calcula la completitud de las postulaciones nuevas y de las que cambiaron desde el último cálculo. Se ejecuta sola cada 5 minutos.";
    if (!lastCompletenessRun) return base;
    const [date = "", time = ""] = lastCompletenessRun.startedAt.split("T");
    const [, month = "", day = ""] = date.split("-");
    const origin = lastCompletenessRun.origin === "MANUAL" ? "manual" : "automática";
    return `${base} Último (${origin}): ${day}-${month} ${time.slice(0, 5)} · ${lastCompletenessRun.status.toLowerCase()} · ${lastCompletenessRun.created} nuevas y ${lastCompletenessRun.changed} con cambios.`;
  }, [lastCompletenessRun, completenessRunning, calculatingCompleteness]);

  const handleSyncRegistros = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg(null);
    setError(null);
    try {
      const result = await syncRegistros();
      setSyncMsg(result.message);
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudieron sincronizar los registros."));
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadDocs = async (code: string) => {
    if (downloadingDocsFor) return;
    setDownloadingDocsFor(code);
    setError(null);
    try {
      const baseUrl = process.env.REACT_APP_API_APPLICANT;
      if (!baseUrl) throw new Error("Configuración faltante.");

      const res = await fetch(`${baseUrl}/application/documents-zip/${encodeURIComponent(code)}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error("Este postulante no tiene documentos cargados.");
        throw new Error(await readApiErrorMessage(res));
      }
      const blob = await res.blob();
      // El backend manda el filename real vía Content-Disposition (1 doc →
      // su nombre original; varios → .zip). El fallback solo se usa si por
      // algún motivo el header no llega.
      triggerBlobDownload(
        blob,
        `documentos_${code}.zip`,
        res.headers.get("Content-Disposition"),
      );
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudieron descargar los documentos."));
    } finally {
      setDownloadingDocsFor(null);
    }
  };

  // POST al endpoint de fichas: recibe la lista de applicationId, responde el
  // binario (1 registro → PDF; varios → .zip) y dispara la descarga. Se envía el
  // userId del usuario actual para la auditoría en la BD (esquema monitoreo).
  const requestFichaDownload = async (applicationIds: string[], fallbackName: string) => {
    const userId  = sessionStorage.getItem("usuarioId") ?? "";
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) throw new Error("Configuración faltante.");

    const res = await fetch(
      `${baseUrl}/application/download-ficha?userId=${encodeURIComponent(userId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ applicationIds }),
      },
    );
    if (!res.ok) {
      if (res.status === 404) throw new Error("No se encontraron postulaciones para generar la(s) ficha(s).");
      throw new Error(await readApiErrorMessage(res));
    }
    const blob = await res.blob();
    triggerBlobDownload(blob, fallbackName, res.headers.get("Content-Disposition"));
  };

  // Descarga la ficha de un registro (acción por fila).
  const handleDownloadFicha = async (item: IApplicationApi) => {
    if (downloadingFichaFor) return;
    if (!item.applicationId) {
      setError("No se pudo identificar la postulación para generar la ficha.");
      return;
    }
    setDownloadingFichaFor(item.code);
    setError(null);
    try {
      await requestFichaDownload([item.applicationId], `ficha_${item.code}.pdf`);
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudo generar la ficha."));
    } finally {
      setDownloadingFichaFor(null);
    }
  };

  // Descarga las fichas de todos los registros seleccionados (acción masiva de
  // la barra de selección). El backend agrupa varios PDFs en un .zip.
  const handleDownloadSelectedFichas = async () => {
    if (downloadingSelectedFichas) return;
    const applicationIds = applications
      .filter((a) => selectedCodes.has(a.code) && a.applicationId)
      .map((a) => a.applicationId);
    if (applicationIds.length === 0) {
      setError("No hay postulaciones seleccionadas para generar fichas.");
      return;
    }
    setDownloadingSelectedFichas(true);
    setError(null);
    try {
      const fallbackName = applicationIds.length === 1
        ? "ficha_postulante.pdf"
        : `fichas_postulantes_${new Date().toISOString().slice(0, 10)}.zip`;
      await requestFichaDownload(applicationIds, fallbackName);
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudieron generar las fichas."));
    } finally {
      setDownloadingSelectedFichas(false);
    }
  };

  // ── selección múltiple ───────────────────────────────────────────────────────
  // Solo las postulaciones en estado DISPONIBLE pueden ser asignadas (y por
  // ende seleccionadas para asignación masiva).
  //
  // Excepción para el reclutador (tw-sel): además de estar DISPONIBLE, la
  // postulación solo es asignable si el formulario es de tipo RECLUTADOR y le
  // pertenece (su recruiterId coincide con el usuarioId de la sesión). El resto
  // de perfiles conserva el comportamiento anterior.
  const isAssignable = useCallback(
    (item: Pick<IApplicationApi, "computedStatus" | "formType" | "recruiterId">) => {
      // El reclutador (tw-sel) conserva su regla: solo DISPONIBLE de sus propios
      // formularios RECLUTADOR.
      if (isRecruiterProfile) {
        return item.computedStatus === "DISPONIBLE"
          && (item.formType || "").toUpperCase() === "RECLUTADOR"
          && !!item.recruiterId
          && item.recruiterId === loggedUserId;
      }
      // Disponible: asignable como siempre (a su cliente o, vía toggle, a otro).
      if (item.computedStatus === "DISPONIBLE") return true;
      // Inhabilitado por cliente/proceso: asignable para reasignar a otro
      // cliente/proceso (el modal fuerza la reasignación). Aplica a cualquier
      // tipo de formulario (CLIENTE, CUENTA, RECLUTADOR).
      if (
        item.computedStatus === "INHABILITADO_CLIENTE" ||
        item.computedStatus === "INHABILITADO_PROCESO"
      ) {
        return true;
      }
      return false;
    },
    [isRecruiterProfile, loggedUserId],
  );

  // Mensaje explicativo cuando la acción de asignar está deshabilitada.
  const notAssignableReason = useCallback(
    (item: Pick<IApplicationApi, "computedStatus" | "formType" | "recruiterId">): string => {
      if (isRecruiterProfile) {
        return "Como reclutador, solo puedes asignar postulantes Disponibles de tus propios formularios de tipo Reclutador";
      }
      return "Solo postulantes Disponibles (o Inhabilitados por cliente/proceso, para reasignar a otro cliente) pueden ser asignados";
    },
    [isRecruiterProfile],
  );

  // Qué postulaciones se pueden SELECCIONAR (checkbox). Es más amplio que
  // "asignable": además de las asignables (Disponible + reglas del reclutador),
  // se permiten las "En proceso activo" y las "Procesado" para descargarlas en
  // masa (fichas / Excel). La asignación masiva sigue restringida solo a las
  // asignables.
  const isSelectable = useCallback(
    (item: Pick<IApplicationApi, "computedStatus" | "formType" | "recruiterId">) => {
      if (item.computedStatus === "PROCESO_ACTIVO") return true;
      if (item.computedStatus === "PROCESADO") return true;
      return isAssignable(item);
    },
    [isAssignable],
  );

  const notSelectableReason = useCallback(
    (item: Pick<IApplicationApi, "computedStatus" | "formType" | "recruiterId">): string => {
      if (isRecruiterProfile && item.computedStatus === "DISPONIBLE") {
        return "Como reclutador, solo puedes seleccionar postulantes Disponibles de tus propios formularios de tipo Reclutador, En proceso activo o Procesado";
      }
      return "Solo se pueden seleccionar postulantes en estado Disponible, En proceso activo o Procesado";
    },
    [isRecruiterProfile],
  );

  const toggleRowSelected = useCallback(
    (code: string) => {
      const target = applications.find((a) => a.code === code);
      if (!target || !isSelectable(target)) return;
      setSelectedCodes((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
      });
    },
    [applications, isSelectable],
  );

  const toggleAllVisibleSelected = useCallback(() => {
    setSelectedCodes((prev) => {
      const selectableCodes = applications
        .filter((a) => a.code && isSelectable(a))
        .map((a) => a.code);
      if (selectableCodes.length === 0) return prev;
      const allSelectableSelected = selectableCodes.every((c) => prev.has(c));
      const next = new Set(prev);
      if (allSelectableSelected) {
        for (const c of selectableCodes) next.delete(c);
      } else {
        for (const c of selectableCodes) next.add(c);
      }
      return next;
    });
  }, [applications, isSelectable]);

  const clearSelection = useCallback(() => setSelectedCodes(new Set()), []);

  const handleAssignSelected = useCallback(() => {
    const items = applications.filter((a) => selectedCodes.has(a.code) && isAssignable(a));
    if (items.length === 0) return;
    setAssignModalItems(items);
  }, [applications, selectedCodes, isAssignable]);

  const handleAssignRow = useCallback(
    (item: IApplicationApi) => {
      if (!isAssignable(item)) return;
      setAssignModalItems([item]);
    },
    [isAssignable],
  );

  // Resetea la selección si cambia la página, los filtros o se recargan datos —
  // evita persistir selecciones de items que ya no están visibles.
  useEffect(() => {
    setSelectedCodes(new Set());
  }, [urlPage, urlLimit, location.search]);

  // ── derived ─────────────────────────────────────────────────────────────────
  const currentPage = pagination?.currentPage ?? urlPage;
  const totalPages  = pagination?.totalPages  ?? 1;
  const totalItems  = pagination?.totalRecords ?? applications.length;
  const pageSize    = pagination?.currentLimit ?? urlLimit;
  const selectableVisibleCodes = applications
    .filter((a) => a.code && isSelectable(a))
    .map((a) => a.code);
  const allVisibleSelected =
    selectableVisibleCodes.length > 0 &&
    selectableVisibleCodes.every((c) => selectedCodes.has(c));
  const someVisibleSelected = selectableVisibleCodes.some((c) => selectedCodes.has(c));
  // Subconjunto asignable de la selección: la asignación masiva sigue operando
  // solo sobre estas (aunque haya "En proceso activo" seleccionadas para descarga).
  const assignableSelectedCount = applications.filter(
    (a) => selectedCodes.has(a.code) && isAssignable(a),
  ).length;

  // ─── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Tab: Postulaciones ──────────────────────────────────────────────── */}
      {activeTab === "postulaciones" && (
        <>
          <style>{`
            .post-row { transition: background 0.12s ease; }
            .post-row:hover { background: rgba(0,101,221,0.038) !important; }
            .post-row:hover td:first-child { border-left-color: #0065DD !important; }
            .postulations-table th,
            .postulations-table td {
              box-sizing: border-box;
              overflow: hidden;
              text-overflow: ellipsis;
              padding-left: 8px !important;
              padding-right: 8px !important;
            }
            .postulations-table th:first-child,
            .postulations-table td:first-child { padding-left: 6px !important; padding-right: 6px !important; }
            .postulations-table-with-actions th:last-child,
            .postulations-table-with-actions td:last-child {
              position: sticky;
              right: 0;
              overflow: visible;
              padding-left: 4px !important;
              padding-right: 4px !important;
            }
            .postulations-table-with-actions th:last-child { z-index: 2; background: #1e3a5f; }
            .postulations-table-with-actions td:last-child { z-index: 1; background: #fff; }
            .postulations-table-with-actions .post-row:hover td:last-child { background: #f7faff !important; }
            .postulations-table-with-actions .post-row-selected td:last-child { background: #eff6ff !important; }
            .postulations-table-scroll-active .postulations-table-with-actions th:last-child,
            .postulations-table-scroll-active .postulations-table-with-actions td:last-child {
              border-left: 1px solid rgba(100, 116, 139, 0.16);
              border-right: 1px solid rgba(100, 116, 139, 0.08);
              box-shadow: -8px 0 12px -14px rgba(15, 23, 42, 0.42);
            }
            .postulations-table-virtualized thead th {
              position: sticky;
              top: 0;
              z-index: 3;
              background: #172d4a;
            }
            .postulations-table-virtualized.postulations-table-with-actions thead th:last-child { z-index: 4; }
            .postulations-table-virtualized tbody tr:not(.post-virtual-spacer) { height: ${TABLE_VIRTUAL_ROW_HEIGHT}px; }
            .postulations-table-virtualized tbody td { white-space: nowrap; }
            .postulations-table-virtualized .post-virtual-spacer td {
              position: static !important;
              padding: 0 !important;
              border: 0 !important;
              background: transparent !important;
              box-shadow: none !important;
            }
            .post-filter-input {
              width: 100%;
              padding: 7px 10px;
              font-size: 12px;
              font-family: Roboto, sans-serif;
              border: 1px solid #e2e8f0;
              border-radius: 7px;
              outline: none;
              color: #334155;
              background: #fff;
              transition: border-color 0.15s, box-shadow 0.15s;
              box-sizing: border-box;
            }
            .post-filter-input:focus {
              border-color: #0065DD;
              box-shadow: 0 0 0 3px rgba(0,101,221,0.1);
            }
            @keyframes header-breathe {
              0%, 100% { opacity: 1; }
              50%       { opacity: 0.82; }
            }
            .post-th-accent { animation: header-breathe 4s ease-in-out infinite; }
            .post-status-dot {
              display: inline-block; width: 7px; height: 7px;
              border-radius: 50%; margin-right: 5px; flex-shrink: 0;
              vertical-align: middle;
            }
            .post-cb {
              appearance: none; -webkit-appearance: none;
              width: 17px; height: 17px; flex-shrink: 0;
              border: 2px solid #cbd5e1;
              border-radius: 5px;
              background: #fff;
              cursor: pointer;
              position: relative;
              transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
              vertical-align: middle;
            }
            .post-cb:hover:not(:disabled) {
              border-color: #0065DD;
              box-shadow: 0 0 0 3px rgba(0,101,221,0.12);
            }
            .post-cb:checked {
              background: #0065DD;
              border-color: #0065DD;
            }
            .post-cb:checked::after {
              content: "";
              position: absolute;
              left: 3px; top: 0px;
              width: 5px; height: 9px;
              border: 2px solid #fff;
              border-top: none; border-left: none;
              transform: rotate(45deg);
            }
            .post-cb:indeterminate {
              background: #0065DD;
              border-color: #0065DD;
            }
            .post-cb:indeterminate::after {
              content: "";
              position: absolute;
              left: 2px; top: 5px;
              width: 9px; height: 2px;
              background: #fff;
              border-radius: 1px;
            }
            .post-cb:disabled { opacity: 0.35; cursor: not-allowed; }
            .descargar-excel-btn { position: relative; overflow: hidden; isolation: isolate; }
            .descargar-excel-btn::after {
              content: ""; position: absolute; top: 0; left: 0;
              width: 40%; height: 100%;
              background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
              transform: translateX(-120%) skewX(-20deg);
              transition: transform 0.4s ease-in-out;
              pointer-events: none; z-index: 1;
            }
            .descargar-excel-btn:hover::after { transform: translateX(260%) skewX(-20deg); }
            .descargar-excel-btn > * { position: relative; z-index: 2; }
            @keyframes post-refresh-spin { to { transform: rotate(360deg); } }
            .post-refresh-icon-spinning { animation: post-refresh-spin 0.85s linear infinite; }
            .postulations-table-dragging,
            .postulations-table-dragging * { cursor: grabbing !important; user-select: none !important; }
          `}</style>

          {/* ── Toolbar ───────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 auto", minWidth: 0, flexWrap: "wrap" }}>
              <motion.button
                onClick={() => setShowFilters((p) => !p)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  display: "flex", alignItems: "center", gap: "7px",
                  padding: "7px 16px", borderRadius: "8px",
                  border: showFilters ? "1.5px solid #0065DD" : "1px solid #e2e8f0",
                  background: showFilters ? "#eff6ff" : "#fff",
                  color: showFilters ? "#0065DD" : "#475569",
                  fontFamily: "Roboto, sans-serif", fontSize: "13px",
                  fontWeight: 500, cursor: "pointer", position: "relative",
                }}
              >
                <TuneIcon style={{ fontSize: 16 }} />
                Filtros
                <AnimatePresence>
                  {totalActiveFilters > 0 && (
                    <motion.span
                      initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                      style={{ position: "absolute", top: "-7px", right: "-7px", width: "18px", height: "18px", borderRadius: "50%", background: "#0065DD", color: "#fff", fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      {totalActiveFilters}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>

              <ColumnVisibilityMenu
                columns={orderedColumnDefinitions}
                visibleKeys={visibleColumnKeys}
                onChange={(keys) => setVisibleColumnKeys(keys as TableColumnKey[])}
                onReorder={(keys) => setColumnOrder(mergeColumnOrder(keys, DEFAULT_COLUMN_ORDER))}
                defaultOrder={DEFAULT_COLUMN_ORDER}
                isSaving={isSavingColumnPreference}
                isSynchronized={isColumnPreferenceSynchronized}
              />

              <TableSearchInput
                value={urlSearch}
                onSearch={updateSearch}
                loading={isFetching}
                placeholder="Buscar postulaciones…"
              />

              <AnimatePresence>
                {totalActiveFilters > 0 && (
                  <motion.button
                    initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    onClick={clearAllFilters}
                    style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fff5f5", color: "#b91c1c", fontFamily: "Roboto, sans-serif", fontSize: "12px", cursor: "pointer" }}
                  >
                    <CloseIcon style={{ fontSize: 13 }} />
                    Limpiar filtros
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Asignación masiva oculta para el reclutador (tw-sel) — de forma
                  momentánea; el resto de perfiles la conserva. Solo cuenta el
                  subconjunto asignable de la selección (ignora "En proceso activo"). */}
              <AnimatePresence>
                {!isRecruiterProfile && assignableSelectedCount > 0 && (
                  <motion.button
                    key="bulk-assign"
                    initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    onClick={handleAssignSelected}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #1d4ed8",
                      background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                      color: "#fff",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(29,78,216,0.22)",
                    }}
                  >
                    <PersonAddAlt1Icon style={{ fontSize: 16 }} />
                    Asignar {assignableSelectedCount === 1 ? "1 postulante" : `${assignableSelectedCount} postulantes`}
                  </motion.button>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {selectedCodes.size > 0 && (
                  <motion.button
                    key="bulk-ficha"
                    initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    onClick={handleDownloadSelectedFichas}
                    disabled={downloadingSelectedFichas}
                    whileHover={{ scale: downloadingSelectedFichas ? 1 : 1.02 }}
                    whileTap={{ scale: downloadingSelectedFichas ? 1 : 0.97 }}
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #b91c1c",
                      background: downloadingSelectedFichas
                        ? "#fca5a5"
                        : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      color: "#fff",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: downloadingSelectedFichas ? "wait" : "pointer",
                      boxShadow: "0 2px 8px rgba(185,28,28,0.22)",
                    }}
                  >
                    {downloadingSelectedFichas ? (
                      <CircularProgress size={15} thickness={5} sx={{ color: "#fff" }} />
                    ) : (
                      <PictureAsPdfIcon style={{ fontSize: 16 }} />
                    )}
                    {downloadingSelectedFichas
                      ? "Generando…"
                      : `Descargar ${selectedCodes.size === 1 ? "ficha" : "fichas"} (${selectedCodes.size})`}
                  </motion.button>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {syncUnlocked && (
                  <motion.button
                    key="sync-registros-btn"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={handleSyncRegistros}
                    disabled={syncing}
                    whileHover={{ scale: syncing ? 1 : 1.02 }}
                    whileTap={{ scale: syncing ? 1 : 0.97 }}
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #4338ca",
                      background: syncing ? "#e0e7ff" : "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
                      color: syncing ? "#4338ca" : "#fff",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: syncing ? "wait" : "pointer",
                      boxShadow: "0 2px 8px rgba(67,56,202,0.22)",
                      opacity: syncing ? 0.8 : 1,
                    }}
                  >
                    {syncing ? (
                      <CircularProgress size={15} thickness={5} sx={{ color: "#4338ca" }} />
                    ) : (
                      <SyncIcon style={{ fontSize: 16 }} />
                    )}
                    {syncing ? "Sincronizando…" : "Sincronizar registros"}
                  </motion.button>
                )}
              </AnimatePresence>

              {canMigratePersonalData && (
                <Tooltip text={migratePersonalDataTooltip} placement="bottom" textTransform="none">
                  <motion.button
                    type="button"
                    onClick={handleMigratePersonalData}
                    disabled={personalDataBusy}
                    whileHover={{ scale: personalDataBusy ? 1 : 1.02 }}
                    whileTap={{ scale: personalDataBusy ? 1 : 0.97 }}
                    aria-label="Migrar datos de postulantes"
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #0f766e",
                      background: personalDataBusy ? "#ccfbf1" : "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
                      color: personalDataBusy ? "#0f766e" : "#fff",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: personalDataBusy ? "wait" : "pointer",
                      boxShadow: "0 2px 8px rgba(15,118,110,0.22)",
                      opacity: personalDataBusy ? 0.8 : 1,
                    }}
                  >
                    {personalDataBusy ? (
                      <CircularProgress size={15} thickness={5} sx={{ color: "#0f766e" }} />
                    ) : (
                      <CloudSyncIcon style={{ fontSize: 16 }} />
                    )}
                    {personalDataBusy ? "Migrando…" : "Migrar datos de postulantes"}
                  </motion.button>
                </Tooltip>
              )}

              {canMigratePersonalData && (
                <Tooltip text={completenessTooltip} placement="bottom" textTransform="none">
                  <motion.button
                    type="button"
                    onClick={handleCalculateCompleteness}
                    disabled={completenessBusy}
                    whileHover={{ scale: completenessBusy ? 1 : 1.02 }}
                    whileTap={{ scale: completenessBusy ? 1 : 0.97 }}
                    aria-label="Calcular completitud"
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #1d4ed8",
                      background: completenessBusy ? "#dbeafe" : "#fff",
                      color: "#1d4ed8",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: completenessBusy ? "wait" : "pointer",
                      opacity: completenessBusy ? 0.8 : 1,
                    }}
                  >
                    {completenessBusy ? (
                      <CircularProgress size={15} thickness={5} sx={{ color: "#1d4ed8" }} />
                    ) : (
                      <FactCheckIcon style={{ fontSize: 16 }} />
                    )}
                    {completenessBusy ? "Calculando…" : "Calcular completitud"}
                  </motion.button>
                </Tooltip>
              )}

              {canConfigureCompleteness && (
                <Tooltip text="Define qué datos y documentos cuentan para la completitud: una base para todos y excepciones por empresa, cliente o área de negocio." placement="bottom" textTransform="none">
                  <motion.button
                    type="button"
                    onClick={() => setShowCompletenessConfig(true)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    aria-label="Configurar completitud"
                    style={{
                      display: "flex", alignItems: "center", gap: "7px",
                      padding: "7px 16px", borderRadius: "8px",
                      border: "1px solid #cbd5e1", background: "#fff", color: "#334155",
                      fontFamily: "Roboto, sans-serif", fontSize: "13px",
                      fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    <TuneIcon style={{ fontSize: 16 }} />
                    Configurar completitud
                  </motion.button>
                </Tooltip>
              )}
              <ConfiguracionCompletitudModal
                open={showCompletenessConfig}
                onClose={() => setShowCompletenessConfig(false)}
                onSaved={(message) => setSyncMsg(message)}
              />

              <motion.button
                type="button"
                onClick={handleRefreshApplications}
                disabled={isRefreshingApplications || isFetching}
                whileHover={{ scale: isRefreshingApplications || isFetching ? 1 : 1.02 }}
                whileTap={{ scale: isRefreshingApplications || isFetching ? 1 : 0.97 }}
                aria-label="Actualizar postulaciones"
                style={{
                  display: "flex", alignItems: "center", gap: "7px",
                  padding: "7px 16px", borderRadius: "8px",
                  border: "1px solid #0065DD",
                  background: isRefreshingApplications ? "#eff6ff" : "#fff",
                  color: "#0065DD",
                  fontFamily: "Roboto, sans-serif", fontSize: "13px",
                  fontWeight: 600,
                  cursor: isRefreshingApplications || isFetching ? "wait" : "pointer",
                  opacity: isFetching && !isRefreshingApplications ? 0.55 : 1,
                }}
              >
                <RefreshIcon
                  className={isRefreshingApplications ? "post-refresh-icon-spinning" : undefined}
                  style={{ fontSize: 17 }}
                />
                {isRefreshingApplications ? "Actualizando…" : "Actualizar postulaciones"}
              </motion.button>

              {/* El perfil reclutador (tw-sel) no puede exportar el listado. */}
              {!isRecruiterProfile && (
                <motion.button
                  className="descargar-excel-btn"
                  onClick={handleExportExcel}
                  disabled={exporting}
                  whileHover={{ scale: exporting ? 1 : 1.02 }}
                  whileTap={{ scale: exporting ? 1 : 0.97 }}
                  style={{
                    display: "flex", alignItems: "center", gap: "7px",
                    padding: "7px 16px", borderRadius: "8px",
                    border: "1px solid #047857",
                    background: exporting ? "#d1fae5" : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    color: exporting ? "#047857" : "#fff",
                    fontFamily: "Roboto, sans-serif", fontSize: "13px",
                    fontWeight: 600, cursor: exporting ? "wait" : "pointer",
                    boxShadow: "0 2px 8px rgba(4,120,87,0.22)",
                    opacity: exporting ? 0.7 : 1,
                  }}
                >
                  <FileDownloadIcon style={{ fontSize: 16 }} />
                  {exporting
                    ? "Generando…"
                    : selectedCodes.size > 0
                      ? `Descargar Excel (${selectedCodes.size})`
                      : "Descargar Excel"}
                </motion.button>
              )}
            </div>
          </div>

          {/* ── Filter panel ──────────────────────────────────────────────────── */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                style={{ marginBottom: "16px", position: "relative", zIndex: 5 }}
              >
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px 24px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
                    {[...primaryFilterColumns, ...(showMoreFilters ? advancedFilterColumns : [])].map((col) => (
                      <div key={col.key}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                          <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                            {col.label}
                          </span>
                          <AnimatePresence>
                            {urlFilters[col.key].length > 0 && (
                              <motion.span
                                initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                                style={{ background: "#0065DD", color: "#fff", borderRadius: "8px", padding: "0 6px", fontSize: "10px", fontWeight: 700, lineHeight: "16px" }}
                              >
                                {urlFilters[col.key].length}
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </div>

                        {col.key === "DNI" ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                            <textarea
                              className="post-filter-input"
                              style={{ width: "100%", minHeight: "92px", resize: "vertical", lineHeight: 1.4 }}
                              placeholder={"Pega uno o varios RUTs, uno por línea"}
                              value={filterInputs.DNI}
                              onChange={(e) => setFilterInputs((prev) => ({ ...prev, DNI: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                  e.preventDefault();
                                  applyRutFilters();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={applyRutFilters}
                              disabled={!rutCandidates.some((candidate) => candidate.isValid)}
                              style={{
                                alignSelf: "flex-start",
                                padding: "6px 10px",
                                borderRadius: "7px",
                                border: "1px solid #0065DD",
                                background: rutCandidates.some((candidate) => candidate.isValid) ? "#0065DD" : "#e2e8f0",
                                color: rutCandidates.some((candidate) => candidate.isValid) ? "#fff" : "#94a3b8",
                                cursor: rutCandidates.some((candidate) => candidate.isValid) ? "pointer" : "not-allowed",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "11.5px",
                                fontWeight: 600,
                              }}
                            >
                              Agregar RUTs válidos
                            </button>
                          </div>
                        ) : col.isSelect ? (
                          <MultiSelectFilter
                            options={selectOptions[col.key] ?? []}
                            selectedValues={urlFilters[col.key]}
                            onToggle={(value) => toggleSelectFilter(col.key, value)}
                            placeholder={col.placeholder}
                          />
                        ) : (
                          <input
                            type="text"
                            className="post-filter-input"
                            style={{ width: "100%" }}
                            placeholder={col.placeholder}
                            value={filterInputs[col.key]}
                            inputMode={col.key === "vacancy" || col.key === "createdAt" ? "numeric" : "text"}
                            onChange={(e) => {
                              let v = e.target.value;
                              if (col.key === "vacancy")   v = v.replace(/\D/g, "");
                              if (col.key === "createdAt") v = v.replace(/[^0-9-]/g, "");
                              setFilterInputs((p) => ({ ...p, [col.key]: v }));
                            }}
                            onKeyDown={(e) => handleKeyDown(e, col.key)}
                          />
                        )}

                        {col.key === "DNI" && rutCandidates.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "7px" }}>
                            {rutCandidates.map((candidate, index) => (
                              <span
                                key={`${candidate.raw}-${index}`}
                                title={candidate.error ?? "RUT válido"}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "3px 9px",
                                  borderRadius: "14px",
                                  background: candidate.isValid ? "#dcfce7" : "#fee2e2",
                                  border: `1px solid ${candidate.isValid ? "#86efac" : "#fca5a5"}`,
                                  color: candidate.isValid ? "#166534" : "#b91c1c",
                                  fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                }}
                              >
                                {candidate.formatted || candidate.raw.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        )}

                        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "7px", minHeight: "4px" }}>
                          <AnimatePresence>
                            {urlFilters[col.key].map((val) => {
                              const estadoCfg = col.key === "status" ? resolveEstado(val) : null;
                              const label = estadoCfg ? estadoCfg.label : val;
                              const bg = estadoCfg ? estadoCfg.bg : "#e0f2fe";
                              const color = estadoCfg ? estadoCfg.color : "#0369a1";
                              const borderColor = estadoCfg ? `${estadoCfg.color}33` : "#bae6fd";
                              return (
                                <motion.span
                                  key={val}
                                  initial={{ opacity: 0, scale: 0.75 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.75 }}
                                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                  style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px 3px 10px", borderRadius: "14px", background: bg, border: `1px solid ${borderColor}`, color, fontSize: "11px", fontFamily: "Roboto, sans-serif", fontWeight: 500, whiteSpace: "nowrap" }}
                                >
                                  {label}
                                  <button
                                    onClick={() => removeFilter(col.key, val)}
                                    style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", opacity: 0.65, lineHeight: 1 }}
                                  >
                                    <CloseIcon style={{ fontSize: 11 }} />
                                  </button>
                                </motion.span>
                              );
                            })}
                          </AnimatePresence>
                        </div>
                      </div>
                    ))}
                    {advancedFilterColumns.length > 0 && (
                      <div style={{ display: "flex", alignItems: "flex-start", paddingTop: "21px" }}>
                        <motion.button
                          type="button"
                          onClick={() => setShowMoreFilters((current) => !current)}
                          whileHover={{ scale: 1.015 }}
                          whileTap={{ scale: 0.98 }}
                          aria-expanded={showMoreFilters}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            minHeight: "34px",
                            padding: "7px 13px",
                            borderRadius: "8px",
                            border: "1px solid #0065DD",
                            background: showMoreFilters ? "#eff6ff" : "#fff",
                            color: "#0065DD",
                            cursor: "pointer",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {showMoreFilters ? "Menos filtros" : "Más filtros"}
                          <KeyboardArrowDownIcon
                            style={{
                              fontSize: 17,
                              transform: showMoreFilters ? "rotate(180deg)" : "none",
                              transition: "transform 0.18s ease",
                            }}
                          />
                        </motion.button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {exportNotice && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                role="status"
                aria-live="polite"
                style={{
                  marginBottom: "12px",
                  padding: "10px 14px",
                  borderRadius: "9px",
                  border: "1px solid #bfdbfe",
                  background: "#eff6ff",
                  color: "#1e40af",
                  display: "flex",
                  alignItems: "center",
                  gap: "9px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                }}
              >
                <InfoOutlinedIcon style={{ fontSize: 18, flexShrink: 0 }} />
                <span>{exportNotice}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Error banner (when there's data but a later fetch failed) ────── */}
          <AnimatePresence>
            {error && applications.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                style={{
                  marginBottom: "10px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  background: "#fff5f5",
                  border: "1px solid #fecaca",
                  color: "#b91c1c",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", display: "flex", padding: 0 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Sync result banner ────────────────────────────────────────────── */}
          <AnimatePresence>
            {syncMsg && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                style={{
                  marginBottom: "10px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  background: "#eef2ff",
                  border: "1px solid #c7d2fe",
                  color: "#4338ca",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <span>{syncMsg}</span>
                <button
                  onClick={() => setSyncMsg(null)}
                  style={{ background: "none", border: "none", color: "#4338ca", cursor: "pointer", display: "flex", padding: 0 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Table ──────────────────────────────────────────────────────────── */}
          <AnimatePresence>
            {hasHorizontalTableScroll && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                style={{ display: "flex", justifyContent: "flex-end", marginBottom: "7px" }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 9px", borderRadius: "14px", background: canScrollTableRight ? "#eff6ff" : "#f8fafc", border: `1px solid ${canScrollTableRight ? "#bfdbfe" : "#e2e8f0"}`, color: canScrollTableRight ? "#1d4ed8" : "#64748b", fontFamily: "Roboto, sans-serif", fontSize: "11px", fontWeight: 600 }}>
                  <SwapHorizIcon style={{ fontSize: 15 }} />
                  {canScrollTableRight
                    ? "Arrastra la tabla para ver más columnas"
                    : canScrollTableLeft
                      ? "Arrastra para volver a las columnas anteriores"
                      : "Puedes arrastrar la tabla horizontalmente"}
                  {canScrollTableRight && <ChevronRightIcon style={{ fontSize: 15 }} />}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
          <div style={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,0.07), 0 0 0 1px rgba(15,23,42,0.06)", background: "#fff", position: "relative" }}>
            {canScrollTableRight && (
              <div aria-hidden="true" style={{ position: "absolute", top: 0, bottom: "12px", right: showApplicationActions ? "172px" : 0, width: "24px", zIndex: 2, pointerEvents: "none", background: "linear-gradient(90deg, rgba(255,255,255,0), rgba(59,130,246,0.045))" }} />
            )}
            <div
              ref={tableScrollRef}
              data-testid="postulations-table-scroll"
              className={`${hasHorizontalTableScroll ? "postulations-table-scroll-active" : ""}${isDraggingTable ? " postulations-table-dragging" : ""}`.trim() || undefined}
              onScroll={handleTableScroll}
              onPointerDown={handleTablePointerDown}
              onPointerMove={handleTablePointerMove}
              onPointerUp={finishTableDrag}
              onPointerCancel={finishTableDrag}
              onPointerLeave={(event) => {
                if (tableDragRef.current.active && event.buttons === 0) finishTableDrag(event);
              }}
              style={{
                overflowX: "auto",
                overflowY: shouldVirtualizeTable ? "auto" : "hidden",
                maxHeight: shouldVirtualizeTable ? `${TABLE_VIRTUAL_VIEWPORT_HEIGHT}px` : undefined,
                width: "100%",
                cursor: hasHorizontalTableScroll ? (isDraggingTable ? "grabbing" : "grab") : "default",
                touchAction: "pan-y",
              }}
            >
              <table className={`postulations-table${shouldVirtualizeTable ? " postulations-table-virtualized" : ""}${showApplicationActions ? " postulations-table-with-actions" : ""}`} style={{ width: "100%", borderCollapse: "collapse", minWidth: `${tableMinWidth}px`, tableLayout: "fixed" }}>
                <colgroup>
                  {canSelectApplications && <col style={{ width: "38px" }} />}
                  {visibleTableColumns.map((column) => <col key={column.key} style={{ width: `${column.width}px` }} />)}
                  {showApplicationActions && <col style={{ width: "172px" }} />}
                </colgroup>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
                    {canSelectApplications && (
                      <th
                        className="post-th-accent"
                        style={{ padding: "13px 12px", borderBottom: "2px solid rgba(56,189,248,0.22)", width: "36px", textAlign: "center" }}
                      >
                        <input
                          type="checkbox"
                          className="post-cb"
                          aria-label="Seleccionar todas las filas visibles"
                          ref={(el) => {
                            if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                          }}
                          checked={allVisibleSelected}
                          onChange={toggleAllVisibleSelected}
                          disabled={selectableVisibleCodes.length === 0}
                          title={selectableVisibleCodes.length === 0 ? "No hay postulantes Disponibles, En proceso activo o Procesados para seleccionar" : undefined}
                          style={{ cursor: selectableVisibleCodes.length === 0 ? "not-allowed" : undefined, opacity: selectableVisibleCodes.length === 0 ? 0.45 : 1 }}
                        />
                      </th>
                    )}
                    {visibleTableColumns.map(
                      (column) => (
                        <th
                          key={column.key}
                          style={{ padding: "13px 16px", fontSize: "10.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.09em", textAlign: "left", whiteSpace: "nowrap", borderBottom: "2px solid rgba(56,189,248,0.22)" }}
                        >
                          {column.label}
                        </th>
                      ),
                    )}
                    {showApplicationActions && (
                      <th style={{ padding: "13px 16px", fontSize: "10.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.09em", textAlign: "left", whiteSpace: "nowrap", borderBottom: "2px solid rgba(56,189,248,0.22)" }}>
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>

                <motion.tbody
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.14 }}
                >
                  {firstLoad || (isFetching && !isRefreshingApplications) ? (
                    <SkeletonTheme baseColor="#f1f5f9" highlightColor="#e2e8f0">
                      {Array.from({ length: Math.min(urlLimit, TABLE_SKELETON_ROW_LIMIT) }).map((_, idx) => (
                        <tr key={`sk-${idx}`} style={{ background: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                          {canSelectApplications && (
                            <td style={{ padding: "11px 12px", textAlign: "center" }}><Skeleton width={15} height={15} /></td>
                          )}
                          {visibleTableColumns.map((column) => (
                            <td key={column.key} style={{ padding: "11px 16px" }}>
                              <Skeleton width={Math.max(45, Math.min(column.width - 20, 110))} height={column.key === "status" || column.key === "formType" ? 20 : 14} borderRadius={column.key === "status" || column.key === "formType" ? 12 : undefined} />
                            </td>
                          ))}
                          {showApplicationActions && (
                            <td style={{ padding: "11px 16px" }}><Skeleton circle width={24} height={24} /></td>
                          )}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : error && applications.length === 0 ? (
                    <tr>
                      <td colSpan={renderedTableColumnCount} style={{ textAlign: "center", padding: "52px 16px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#b91c1c" }}>
                        {error}
                      </td>
                    </tr>
                  ) : applications.length === 0 ? (
                    <tr>
                      <td colSpan={renderedTableColumnCount} style={{ textAlign: "center", padding: "52px 16px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#94a3b8" }}>
                        No se encontraron postulaciones.
                      </td>
                    </tr>
                  ) : (
                    <>
                    {virtualTopSpacerHeight > 0 && (
                      <tr className="post-virtual-spacer" aria-hidden="true">
                        <td colSpan={renderedTableColumnCount} style={{ height: `${virtualTopSpacerHeight}px`, padding: 0, border: 0 }} />
                      </tr>
                    )}
                    {renderedApplications.map((item, visibleIndex) => {
                      const i = virtualStartIndex + visibleIndex;
                      const estadoCfg = resolveEstado(item.computedStatus);
                      const estadoMsg = buildStatusMessage(item);
                      const isRowSelected = item.code ? selectedCodes.has(item.code) : false;
                      const rowAssignable = isAssignable(item);
                      const rowSelectable = isSelectable(item);
                      const displayedProcessContext = resolveDisplayedProcessContext(item);
                      return (
                        <tr
                          key={item.code ?? i}
                          className={`post-row${isRowSelected ? " post-row-selected" : ""}`}
                          style={{ background: isRowSelected ? "#eff6ff" : "#fff", borderBottom: "1px solid #f1f5f9" }}
                        >
                          {canSelectApplications && (
                            <td style={{ padding: "11px 12px", textAlign: "center" }}>
                              <input
                                type="checkbox"
                                className="post-cb"
                                aria-label={`Seleccionar postulación ${item.code}`}
                                checked={isRowSelected}
                                onChange={() => item.code && toggleRowSelected(item.code)}
                                disabled={!item.code || !rowSelectable}
                                title={!rowSelectable ? notSelectableReason(item) : undefined}
                                style={{ cursor: !rowSelectable ? "not-allowed" : undefined, opacity: !rowSelectable ? 0.45 : 1 }}
                              />
                            </td>
                          )}
                          {renderOrderedCells(visibleTableColumns, {
                            code: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "13px", color: "#0065DD", fontWeight: 600, whiteSpace: "nowrap", borderLeft: "3px solid transparent", transition: "border-color 0.12s" }}>
                                {item.code || "—"}
                                <EtapasPostulacionChip
                                  applicationId={item.applicationId}
                                  stageStatus={item.stageStatus}
                                  completedStages={item.completedStages}
                                  totalStages={item.totalStages}
                                />
                              </td>
                            ),
                            aiValidation: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <PillValidacionCedula veredictoCedula={item.veredictoCedula} detalleCedula={item.detalleCedula} />
                              </td>
                            ),
                            completeness: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <CompletitudPostulacionCell completeness={item.completeness} />
                              </td>
                            ),
                            DNI: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "13px", color: "#334155", whiteSpace: "nowrap" }}>
                                {item.DNI || "—"}
                              </td>
                            ),
                            names: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
                                {/* La señal de validación de cédula dejó de ir
                                    pegada al nombre: ahora es su propia columna
                                    ("Validación"). Acá solo el nombre, truncado
                                    con "…" cuando no cabe (tableLayout fixed). */}
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {item.names || "—"}
                                </span>
                              </td>
                            ),
                            gender: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.gender || "—"}
                              </td>
                            ),
                            phone: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "12.5px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.phone || "—"}
                              </td>
                            ),
                            formCode: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "13px", color: "#0065DD", fontWeight: 600, whiteSpace: "nowrap" }}>
                                {item.formCode || "—"}
                              </td>
                            ),
                            formType: () => (
                              <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                              {(() => {
                                const tipoCfg = TIPO_CONFIG[String(item.formType || "").toUpperCase()];
                                if (!tipoCfg) return <span style={{ color: "#94a3b8" }}>—</span>;
                                return (
                                  <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: "12px", background: tipoCfg.bg, color: tipoCfg.color, fontSize: "11.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", border: `1px solid ${tipoCfg.color}22` }}>
                                    {tipoCfg.label}
                                  </span>
                                );
                              })()}
                              </td>
                            ),
                            formCategory: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.formCategory || "—"}
                              </td>
                            ),
                            client: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569" }}>
                                {item.client || "—"}
                              </td>
                            ),
                            shift: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.shift || "—"}
                              </td>
                            ),
                            position: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.position || "—"}
                              </td>
                            ),
                            locality: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#475569", whiteSpace: "nowrap" }}>
                                {item.locality || "—"}
                              </td>
                            ),
                            businessArea: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "12px", color: "#64748b", whiteSpace: "nowrap" }}>
                                {displayedProcessContext.businessArea || "—"}
                              </td>
                            ),
                            vacancy: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "'Courier New', monospace", fontSize: "12px", color: "#64748b" }}>
                                {displayedProcessContext.processId || "—"}
                              </td>
                            ),
                            recruiter: () => (
                              <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                                {displayedProcessContext.recruiter || "—"}
                              </td>
                            ),
                            createdAt: () => (
                              <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                              {(() => {
                                const parts = formatDateParts(item.createdAt);
                                if (!parts) return <span style={{ color: "#94a3b8" }}>—</span>;
                                return (
                                  <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>{parts.date}</span>
                                    <span style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "11px", color: "#94a3b8" }}>{parts.time}</span>
                                  </div>
                                );
                              })()}
                              </td>
                            ),
                            abVariant: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <VarianteABCell item={item} />
                              </td>
                            ),
                            formStage: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <EtapaFormularioCell item={item} />
                              </td>
                            ),
                            stageWhatsapp: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <WhatsappEtapaCell
                                  item={item}
                                  onSendNow={() => handleSendInvitationNow(item)}
                                  onExtend={() => handleExtendInvitation(item)}
                                  busy={invitationActionFor === item.applicationId}
                                />
                              </td>
                            ),
                            status: () => (
                              <td style={{ padding: "11px 16px" }}>
                                <Tooltip text={estadoMsg} textTransform="none">
                                  <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: "14px", background: estadoCfg.bg, color: estadoCfg.color, fontSize: "11.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap", border: `1px solid ${estadoCfg.color}22`, cursor: "help" }}>
                                    <span className="post-status-dot" style={{ background: estadoCfg.dot, boxShadow: `0 0 4px ${estadoCfg.dot}88` }} />
                                    {estadoCfg.label}
                                  </span>
                                </Tooltip>
                              </td>
                            ),
                          })}
                          {showApplicationActions && (
                            <td style={{ padding: "11px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                              {/* Una sola acción: el modal de la postulación incluye "Validar datos"
                                  cuando el veredicto de cédula admite intervención manual. */}
                              <Tooltip text={puedeValidarManualmente(item.veredictoCedula) ? "Ver y validar postulación" : "Ver postulación"}>
                                <motion.button
                                  onClick={() => setSelected(item)}
                                  whileHover={{ scale: 1.18, backgroundColor: "rgba(0,101,221,0.08)" }}
                                  whileTap={{ scale: 0.88 }}
                                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "#0065DD" }}
                                >
                                  <VisibilityIcon style={{ fontSize: 18 }} />
                                </motion.button>
                              </Tooltip>
                              {/* Asignación individual oculta para el reclutador (tw-sel). */}
                              {!isRecruiterProfile && (
                                <Tooltip text={rowAssignable ? "Asignar postulante" : notAssignableReason(item)}>
                                  <motion.button
                                    onClick={() => handleAssignRow(item)}
                                    disabled={!rowAssignable}
                                    whileHover={rowAssignable ? { scale: 1.18, backgroundColor: "rgba(5,150,105,0.08)" } : {}}
                                    whileTap={rowAssignable ? { scale: 0.88 } : {}}
                                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "8px", border: "none", background: "transparent", cursor: rowAssignable ? "pointer" : "not-allowed", color: rowAssignable ? "#059669" : "#cbd5e1", opacity: rowAssignable ? 1 : 0.55 }}
                                  >
                                    <PersonAddAlt1Icon style={{ fontSize: 18 }} />
                                  </motion.button>
                                </Tooltip>
                              )}
                              {(() => {
                                const isThisRowDownloading = downloadingDocsFor === item.code;
                                const isOtherRowDownloading = downloadingDocsFor !== null && downloadingDocsFor !== item.code;
                                const isBusy = isThisRowDownloading || isOtherRowDownloading;
                                const tooltipText = isThisRowDownloading
                                  ? "Descargando documentos…"
                                  : isOtherRowDownloading
                                    ? "Espera a que termine la descarga en curso"
                                    : "Descargar documentos";
                                return (
                                  <Tooltip text={tooltipText}>
                                    <motion.button
                                      onClick={() => handleDownloadDocs(item.code)}
                                      disabled={isBusy}
                                      whileHover={{ scale: isBusy ? 1 : 1.18, backgroundColor: isBusy ? "transparent" : "rgba(124,58,237,0.08)" }}
                                      whileTap={{ scale: isBusy ? 1 : 0.88 }}
                                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "8px",
                                        border: "none",
                                        background: "transparent",
                                        cursor: isThisRowDownloading ? "wait" : isOtherRowDownloading ? "not-allowed" : "pointer",
                                        color: isBusy ? "#cbd5e1" : "#7c3aed",
                                        opacity: isOtherRowDownloading ? 0.5 : 1,
                                      }}
                                    >
                                      {isThisRowDownloading ? (
                                        <CircularProgress size={16} thickness={5} sx={{ color: "#7c3aed" }} />
                                      ) : (
                                        <FolderZipIcon style={{ fontSize: 18 }} />
                                      )}
                                    </motion.button>
                                  </Tooltip>
                                );
                              })()}
                              {(() => {
                                const isThisRowDownloading = downloadingFichaFor === item.code;
                                const isOtherRowDownloading = downloadingFichaFor !== null && downloadingFichaFor !== item.code;
                                const isBusy = isThisRowDownloading || isOtherRowDownloading;
                                const tooltipText = isThisRowDownloading
                                  ? "Generando ficha…"
                                  : isOtherRowDownloading
                                    ? "Espera a que termine la descarga en curso"
                                    : "Descargar ficha";
                                return (
                                  <Tooltip text={tooltipText}>
                                    <motion.button
                                      onClick={() => handleDownloadFicha(item)}
                                      disabled={isBusy}
                                      whileHover={{ scale: isBusy ? 1 : 1.18, backgroundColor: isBusy ? "transparent" : "rgba(220,38,38,0.08)" }}
                                      whileTap={{ scale: isBusy ? 1 : 0.88 }}
                                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "8px",
                                        border: "none",
                                        background: "transparent",
                                        cursor: isThisRowDownloading ? "wait" : isOtherRowDownloading ? "not-allowed" : "pointer",
                                        color: isBusy ? "#cbd5e1" : "#dc2626",
                                        opacity: isOtherRowDownloading ? 0.5 : 1,
                                      }}
                                    >
                                      {isThisRowDownloading ? (
                                        <CircularProgress size={16} thickness={5} sx={{ color: "#dc2626" }} />
                                      ) : (
                                        <PictureAsPdfIcon style={{ fontSize: 18 }} />
                                      )}
                                    </motion.button>
                                  </Tooltip>
                                );
                              })()}
                            </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {virtualBottomSpacerHeight > 0 && (
                      <tr className="post-virtual-spacer" aria-hidden="true">
                        <td colSpan={renderedTableColumnCount} style={{ height: `${virtualBottomSpacerHeight}px`, padding: 0, border: 0 }} />
                      </tr>
                    )}
                    </>
                  )}
                </motion.tbody>
              </table>
            </div>
          </div>

          {/* ── Pagination ──────────────────────────────────────────────────── */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          {/* ── Modal postulación (vista + validación manual) ─────────────────── */}
          <AnimatePresence>
            {selected && (
              <PostulacionModal
                postulacion={selected}
                onClose={() => setSelected(null)}
                onValidated={() => {
                  // Refresca el listado para reflejar el nuevo estado (validado_manual)
                  // y cualquier dato editado, con el mismo mecanismo del botón
                  // "Actualizar postulaciones".
                  lastFetchKeyRef.current = null;
                  backgroundRefreshRef.current = true;
                  setIsRefreshingApplications(true);
                  setRefreshKey((current) => current + 1);
                }}
              />
            )}
          </AnimatePresence>

          {/* ── Modal asignar postulantes ──────────────────────────────────────── */}
          <ModalAsignarPostulantes
            open={!!assignModalItems}
            items={assignModalItems ?? []}
            onClose={() => {
              setAssignModalItems(null);
              clearSelection();
            }}
            onAssign={async (payload) => {
              const baseUrl = process.env.REACT_APP_API_APPLICANT;
              if (!baseUrl) {
                setError("Configuración faltante: REACT_APP_API_APPLICANT");
                throw new Error("Configuración faltante: REACT_APP_API_APPLICANT");
              }
              const userId = sessionStorage.getItem("usuarioId") ?? "";
              const assignments = payload
                .map(({ item, config }) => {
                  // Para formularios tipo RECLUTADOR el proceso, área y reclutador
                  // ya quedan fijos al crear el formulario, así que se autollenan
                  // desde el item cuando la config viene vacía (no hay UI para editarlos).
                  const isReclutador = (item.formType || "").toUpperCase() === "RECLUTADOR";
                  // Reasignación a otro cliente: cuando el modal fijó un cliente
                  // destino distinto al original, se envía para revalidar la
                  // inhabilitación del destino y dejar traza (cliente X → Z).
                  const targetClientId = config.targetClientId;
                  return {
                    code: item.code,
                    personId: item.personId,
                    vacancyId: config.vacancyId || (isReclutador ? item.vacancy : undefined),
                    recruiterId: config.recruiterId || (isReclutador ? item.recruiterId : undefined),
                    businessAreaCode:
                      config.businessAreaCode
                      || (isReclutador ? (item.businessAreaCode || item.businessArea) : undefined),
                    targetClientId,
                    targetClient: config.targetClientName,
                    originClientId: item.clientId,
                    originClient: item.client,
                  };
                })
                .filter((a) => a.code && a.personId && a.vacancyId && a.recruiterId);
              if (assignments.length === 0) {
                throw new Error("Completa vacante y reclutador para los postulantes a asignar.");
              }
              const token = sessionStorage.getItem("token") ?? "";
              const res = await fetch(
                `${baseUrl}/application/assign?userId=${encodeURIComponent(userId)}`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify({ assignments }),
                },
              );
              if (!res.ok) throw new Error(await readApiErrorMessage(res));
              const data = await res.json() as {
                totalAssigned: number;
                totalRequested: number;
                results: {
                  personId: string;
                  name?: string;
                  DNI?: string;
                  assigned: boolean;
                  reason?: string;
                  crossClient?: boolean;
                  originClient?: string;
                  targetClient?: string;
                }[];
              };
              setError(null);
              // refrescar la tabla para reflejar los estados actualizados
              lastFetchKeyRef.current = null;
              setRefreshKey((prev) => prev + 1);
              clearSelection();
              return {
                totalAssigned: data.totalAssigned,
                totalRequested: data.totalRequested,
                results: data.results,
              };
            }}
          />
        </>
      )}

      {/* ── Tab: Formularios ────────────────────────────────────────────────── */}
      {activeTab === "formularios" && <TablaFormularios />}
    </>
  );
};

export default TablaPostulaciones;
